import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CommunicationHistory from "@/components/CommunicationHistory";
import {
  ArrowLeft, Save, Trash2, Loader2, ExternalLink, AlertTriangle,
  Plus, X, CheckCircle, CalendarDays, ScrollText,
  Download, Mail, Bell, Send, Eye, History, CreditCard, RefreshCw, Scissors, Upload,
} from "lucide-react";
import { format } from "date-fns";
import ReviewRequestButton from "@/components/ReviewRequestButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Constants ────────────────────────────────────────────────────────────────

const SOURCE_TYPE_LABELS = {
  flight_booking: "Flight Booking",
  visa_quote:     "Visa Quote",
  service_entry:  "Service Entry",
  hotel_booking:  "Hotel Booking",
  manual:         "Manual",
  other:          "Other",
};

const INVOICE_STATUSES = ["draft", "sent", "partially_paid", "paid", "overdue", "cancelled"];
const PAYMENT_STATUSES = ["not_requested", "payment_pending", "partially_paid", "paid", "refunded"];
const CURRENCIES       = ["EUR", "GBP", "USD", "AED"];
const CURRENCY_SYMBOLS = { EUR: "€", GBP: "£", USD: "$", AED: "د.إ" };

const VAT_OPTIONS = [
  { value: "Dutch VAT 21%",              note: "VAT charged at 21% (Dutch standard rate)" },
  { value: "Reverse Charge",             note: "Reverse charge mechanism applies — VAT not charged (Article 196 EU VAT Directive)" },
  { value: "Outside Scope of Dutch VAT", note: "Service outside the scope of Dutch VAT — non-EU customer (Article 44 EU VAT Directive)" },
  { value: "VAT Exempt",                 note: "VAT exempt — no VAT charged" },
];
const VAT_NOTE_MAP = Object.fromEntries(VAT_OPTIONS.map(o => [o.value, o.note]));

const LINE_TYPES = [
  "service_fee", "flight_ticket", "consultancy", "visa_fee",
  "government_fee", "supplier_fee", "hotel", "insurance", "other", "discount",
];
const LINE_TYPE_LABELS = {
  service_fee:    "Service Fee",
  flight_ticket:  "Flight Ticket",
  consultancy:    "Consultancy",
  visa_fee:       "Visa Fee",
  government_fee: "Government Fee",
  supplier_fee:   "Supplier Fee",
  hotel:          "Hotel",
  insurance:      "Insurance",
  other:          "Other",
  discount:       "Discount",
};

const SCHEDULE_PRESETS = [
  { value: "full_upfront", label: "Full Upfront" },
  { value: "pay_later",    label: "Pay Later" },
  { value: "equal_2",      label: "2 Equal Installments" },
  { value: "equal_3",      label: "3 Equal Installments" },
  { value: "custom",       label: "Custom" },
];

const PAYMENT_METHODS = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "card",          label: "Card" },
  { value: "cash",          label: "Cash" },
  { value: "tikkie",        label: "Tikkie" },
  { value: "other",         label: "Other" },
];

const SCHED_STATUS_META = {
  pending: { label: "Pending", cls: "bg-slate-100 text-slate-600" },
  paid:    { label: "Paid",    cls: "bg-green-100 text-green-700" },
  overdue: { label: "Overdue", cls: "bg-red-100 text-red-700" },
  waived:  { label: "Waived",  cls: "bg-slate-100 text-slate-400" },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const round2 = n => Math.round(n * 100) / 100;

function uid() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;
}

function blankLine() {
  return { id: uid(), description: "", quantity: "1", unit_price: "", amount: "", vat_type: "", vat_amount: "", line_type: "other" };
}

function blankInstallment(total = 0) {
  return {
    id: uid(), label: "Payment",
    amount: total > 0 ? String(total) : "", percentage: total > 0 ? "100" : "",
    due_date: "", due_event: "", status: "pending",
    paid_date: null, paid_amount: null, payment_method: null, payment_reference: null,
  };
}

function computeLineAmount(qty, price) {
  return round2((parseFloat(qty) || 0) * (parseFloat(price) || 0));
}
function computeLineVat(amount, vatType) {
  return vatType === "Dutch VAT 21%" ? round2(amount * 0.21) : 0;
}

function generateSchedule(type, total, invoiceDate) {
  const today = invoiceDate || format(new Date(), "yyyy-MM-dd");
  const mk = (label, amount, pct, dueDate, dueEvent) => ({
    id: uid(), label,
    amount: String(amount), percentage: String(pct),
    due_date: dueDate || "", due_event: dueEvent || "",
    status: "pending", paid_date: null, paid_amount: null, payment_method: null, payment_reference: null,
  });
  switch (type) {
    case "full_upfront":
      return [mk("Full Payment", total, 100, today, "Full payment due on invoice date")];
    case "pay_later":
      return [mk("Full Payment", total, 100, "", "Pay later — bank transfer")];
    case "equal_2": {
      const half = round2(total / 2);
      return [
        mk("1st Installment", half, 50, today, "1st of 2 equal installments"),
        mk("2nd Installment", round2(total - half), 50, "", "2nd of 2 equal installments"),
      ];
    }
    case "equal_3": {
      const third = round2(total / 3);
      const rest  = round2(total - third * 2);
      const pct1  = round2(100 / 3);
      return [
        mk("1st Installment", third, pct1, today, "1st of 3 equal installments"),
        mk("2nd Installment", third, pct1, "", "2nd of 3 equal installments"),
        mk("3rd Installment", rest, round2(100 - pct1 * 2), "", "3rd of 3 equal installments"),
      ];
    }
    default: // custom
      return [mk("Payment", 0, 0, "", "")];
  }
}

// ── Form mapping ─────────────────────────────────────────────────────────────

const PAYMENT_METHOD_OPTIONS = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "tikkie",        label: "Tikkie" },
  { value: "stripe_link",   label: "Stripe / Online" },
  { value: "cash",          label: "Cash" },
  { value: "other",         label: "Other" },
];

const BLANK_FORM = {
  invoice_number: "", source_type: "manual", source_id: "", quote_reference: "",
  customer_name: "", customer_email: "", customer_address: "",
  invoice_date: format(new Date(), "yyyy-MM-dd"), due_date: "",
  currency: "EUR", amount_received: "",
  invoice_status: "draft", payment_status: "not_requested",
  vat_type: "", vat_note: "", notes: "",
  external_invoice_source: "", external_invoice_reference: "",
  // Payment instructions
  payment_methods: ["bank_transfer"],
  payment_beneficiary_name: "", payment_iban: "", payment_bic: "",
  payment_bank_name: "", payment_tikkie_url: "", payment_stripe_url: "",
  payment_reference_override: "", payment_custom_instructions: "",
  pay_later_flight: false,
  // Bookkeeping
  supplier_name: "", supplier_reference: "", supplier_cost: "",
  payment_mode: "", supplier_paid_date: "", date_received: "", bank_fee: "",
  payment_processor: "", payout_date: "", reconciled: false,
  supplier_receipt_url: "",
};

function toDisplayForm(inv) {
  return {
    invoice_number:   inv.invoice_number   || "",
    source_type:      inv.source_type      || "manual",
    source_id:        inv.source_id        || "",
    quote_reference:  inv.quote_reference  || "",
    customer_name:    inv.customer_name    || "",
    customer_email:   inv.customer_email   || "",
    customer_address: inv.customer_address || "",
    invoice_date:     inv.invoice_date     || "",
    due_date:         inv.due_date         || "",
    currency:         inv.currency         || "EUR",
    amount_received:  inv.amount_received  != null ? String(inv.amount_received) : "",
    invoice_status:   inv.invoice_status   || "draft",
    payment_status:   inv.payment_status   || "not_requested",
    vat_type:         inv.vat_type         || "",
    vat_note:         inv.vat_note         || "",
    notes:            inv.notes            || "",
    external_invoice_source:    inv.external_invoice_source    || "",
    external_invoice_reference: inv.external_invoice_reference || "",
    payment_methods:             inv.payment_methods             || ["bank_transfer"],
    payment_beneficiary_name:    inv.payment_beneficiary_name    || "",
    payment_iban:                inv.payment_iban                || "",
    payment_bic:                 inv.payment_bic                 || "",
    payment_bank_name:           inv.payment_bank_name           || "",
    payment_tikkie_url:          inv.payment_tikkie_url          || "",
    payment_stripe_url:          inv.payment_stripe_url          || "",
    payment_reference_override:  inv.payment_reference_override  || "",
    payment_custom_instructions: inv.payment_custom_instructions || "",
    pay_later_flight:            inv.pay_later_flight            || false,
    // Bookkeeping
    supplier_name:      inv.supplier_name      || "",
    supplier_reference: inv.supplier_reference || "",
    supplier_cost:      inv.supplier_cost      != null ? String(inv.supplier_cost) : "",
    payment_mode:       inv.payment_mode       || "",
    supplier_paid_date: inv.supplier_paid_date || "",
    date_received:      inv.date_received      || "",
    bank_fee:           inv.bank_fee           != null ? String(inv.bank_fee) : "",
    payment_processor:  inv.payment_processor  || "",
    payout_date:        inv.payout_date        || "",
    reconciled:         inv.reconciled         || false,
    supplier_receipt_url: inv.supplier_receipt_url || "",
  };
}

function toDisplayLines(inv) {
  if (!inv.line_items?.length) return [blankLine()];
  return inv.line_items.map(li => ({
    id:          li.id || uid(),
    description: li.description || "",
    quantity:    li.quantity    != null ? String(li.quantity)    : "1",
    unit_price:  li.unit_price  != null ? String(li.unit_price)  : "",
    amount:      li.amount      != null ? String(li.amount)      : "",
    vat_type:    li.vat_type    || "",
    vat_amount:  li.vat_amount  != null ? String(li.vat_amount)  : "",
    line_type:   li.line_type   || "other",
  }));
}

function toDisplaySchedule(inv) {
  if (!inv.payment_schedule?.length) return [];
  return inv.payment_schedule.map(item => ({
    id:                item.id || uid(),
    label:             item.label             || "Payment",
    amount:            item.amount            != null ? String(item.amount)      : "",
    percentage:        item.percentage        != null ? String(item.percentage)  : "",
    due_date:          item.due_date          || "",
    due_event:         item.due_event         || "",
    status:            item.status            || "pending",
    paid_date:         item.paid_date         || null,
    paid_amount:       item.paid_amount       != null ? String(item.paid_amount) : null,
    payment_method:    item.payment_method    || null,
    payment_reference: item.payment_reference || null,
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function InvoiceDetail() {
  const { invoiceId }  = useParams();
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthHeaders } = useAuth();

  const isNew = invoiceId === "new";

  const [loading,  setLoading]  = useState(!isNew);
  const [saving,        setSaving]       = useState(false);
  const [deleting,      setDeleting]     = useState(false);
  const [invoice,       setInvoice]      = useState(null);
  const [linkedBooking, setLinkedBooking] = useState(null);
  const [form,     setForm]     = useState(BLANK_FORM);
  const [lines,    setLines]    = useState([blankLine()]);
  const [schedule, setSchedule] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [schedulePreset, setSchedulePreset] = useState("full_upfront");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExternalAccounting, setShowExternalAccounting] = useState(false);
  const [showBookkeeping, setShowBookkeeping] = useState(false);
  const [receiptUploading, setReceiptUploading] = useState(false);
  const receiptFileRef = useRef();
  const [showRevisions, setShowRevisions] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [paymentOverride, setPaymentOverride] = useState(false);
  const [paymentSettingsPreview, setPaymentSettingsPreview] = useState(null);
  const [generatingTikkie, setGeneratingTikkie] = useState(false);

  // Payment recording state
  const [recordingId, setRecordingId] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    paid_amount: "", paid_date: format(new Date(), "yyyy-MM-dd"),
    payment_method: "bank_transfer", payment_reference: "",
  });
  const [processingPayment, setProcessingPayment] = useState(false);

  // PDF / email / reminder state
  const [downloadingPdf, setDownloadingPdf]   = useState(false);
  const [previewingPdf,  setPreviewingPdf]    = useState(false);
  const [pdfVersions,    setPdfVersions]      = useState([]);
  const [sendingEmail,   setSendingEmail]     = useState(null); // "test" | "customer"
  const [reminders,      setReminders]        = useState([]);
  const [sendingReminder, setSendingReminder] = useState(null); // "test" | "customer"
  const [reminderType,   setReminderType]     = useState("unpaid_invoice");
  const [reminderInstId, setReminderInstId]   = useState("");

  // Email preview modal
  const [emailPreview, setEmailPreview]       = useState(null); // { type, recipient, subject, attachment, action }

  // Cancellation / Refund state
  const [cancellationRecord,  setCancellationRecord]  = useState(null);
  const [showCancellation,    setShowCancellation]    = useState(false);
  const [cancellationSaving,  setCancellationSaving]  = useState(false);
  const [generatingDoc,       setGeneratingDoc]       = useState(null); // "credit_note" | "cancellation_doc"
  const [previewingDoc,       setPreviewingDoc]       = useState(null);
  const [sendingDocEmail,     setSendingDocEmail]     = useState(false);
  const [sendingDocTestEmail, setSendingDocTestEmail] = useState(false);
  const BLANK_CANCELLATION_FORM = {
    customer_refund_amount: "", customer_refund_date: "", customer_refund_method: "",
    supplier_refund_amount: "", supplier_refund_date: "", supplier_refund_notes: "",
    cancellation_fee_retained: "", unrecovered_payment_fee: "", refund_processing_fee: "",
    cancellation_date: "", vat_adjustment_amount: "", cancellation_reason: "", internal_notes: "",
    refund_method: "", refund_days: "", refund_days_type: "",
  };
  const [cancellationForm, setCancellationForm] = useState(BLANK_CANCELLATION_FORM);

  // Discount panel state
  const [discountType,  setDiscountType]  = useState("fixed"); // "fixed" | "percentage"
  const [discountValue, setDiscountValue] = useState("");
  const [discountDesc,  setDiscountDesc]  = useState("Discount / goodwill adjustment");
  const [discountNote,  setDiscountNote]  = useState("");

  // ── Derived totals ───────────────────────────────────────────────────────────
  const subtotal     = round2(lines.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0));
  const vatTotal     = round2(lines.reduce((s, li) => s + (parseFloat(li.vat_amount) || 0), 0));
  const invoiceTotal = round2(subtotal + vatTotal);

  const scheduleAmountReceived = round2(
    schedule.filter(i => i.status === "paid").reduce((s, i) => s + (parseFloat(i.paid_amount) || 0), 0)
  );
  const hasSchedule = schedule.length > 0;
  const effectiveReceived = hasSchedule
    ? scheduleAmountReceived
    : (parseFloat(form.amount_received) || 0);
  const balanceDue = round2(invoiceTotal - effectiveReceived);
  const paymentEnteredAmt   = parseFloat(paymentForm.paid_amount) || 0;
  const paymentWouldOverpay = paymentEnteredAmt > balanceDue + 0.005;

  const today = format(new Date(), "yyyy-MM-dd");
  const amountDueNow = round2(
    schedule.filter(i => (i.status === "pending" || i.status === "overdue") && (!i.due_date || i.due_date <= today))
      .reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)
  );
  const futureAmount = round2(
    schedule.filter(i => i.status === "pending" && i.due_date && i.due_date > today)
      .reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)
  );

  // ── Effects ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isNew) {
      const pre = { ...BLANK_FORM };
      pre.source_type     = searchParams.get("source_type")     || "manual";
      pre.source_id       = searchParams.get("source_id")       || "";
      pre.quote_reference = searchParams.get("quote_reference") || "";
      pre.customer_name   = searchParams.get("customer_name")   || "";
      pre.customer_email  = searchParams.get("customer_email")  || "";
      pre.currency        = searchParams.get("currency")        || "EUR";
      pre.invoice_date    = searchParams.get("invoice_date")    || format(new Date(), "yyyy-MM-dd");
      pre.vat_type        = searchParams.get("vat_type")        || "";
      pre.vat_note        = searchParams.get("vat_note")        || "";
      const seedTotal = parseFloat(searchParams.get("invoice_total")) || 0;
      if (seedTotal > 0) {
        const seedLine = blankLine();
        seedLine.description = pre.source_type === "visa_quote" ? "Visa service fee"
          : pre.source_type === "flight_booking" ? "Flight ticket"
          : pre.source_type === "hotel_booking" ? "Hotel accommodation"
          : "Service";
        seedLine.quantity   = "1";
        seedLine.unit_price = String(seedTotal);
        seedLine.amount     = String(seedTotal);
        seedLine.vat_type   = pre.vat_type;
        seedLine.vat_amount = String(computeLineVat(seedTotal, pre.vat_type));
        seedLine.line_type  = pre.source_type === "visa_quote" ? "visa_fee"
          : pre.source_type === "flight_booking" ? "flight_ticket"
          : pre.source_type === "hotel_booking" ? "hotel"
          : "service_fee";
        setLines([seedLine]);
      }
      setForm(pre);
    }
  }, [isNew, searchParams]);

  useEffect(() => {
    if (!isNew) {
      setLoading(true);
      axios.get(`${API}/invoices/${invoiceId}`, { headers: getAuthHeaders() })
        .then(r => {
          setInvoice(r.data);
          setForm(toDisplayForm(r.data));
          setLines(toDisplayLines(r.data));
          setSchedule(toDisplaySchedule(r.data));
          if (r.data.external_invoice_source || r.data.external_invoice_reference) {
            setShowExternalAccounting(true);
          }
          const hasOverride = !!(
            r.data.payment_iban || r.data.payment_tikkie_url || r.data.payment_stripe_url ||
            r.data.payment_custom_instructions || r.data.payment_beneficiary_name
          );
          if (hasOverride) {
            setPaymentOverride(true);
          }
          // Fetch linked booking status for flight invoices
          if (r.data.source_type === "flight_booking" && r.data.source_id) {
            axios.get(`${API}/invoices/${invoiceId}/linked-booking`, { headers: getAuthHeaders() })
              .then(lb => setLinkedBooking(lb.data))
              .catch(() => {});
          }
        })
        .catch(() => { toast.error("Invoice not found"); navigate("/invoices"); })
        .finally(() => setLoading(false));
    }
  }, [invoiceId, isNew]);

  useEffect(() => {
    if (!isNew && invoiceId) {
      axios.get(`${API}/invoices/${invoiceId}/receipts`, { headers: getAuthHeaders() })
        .then(r => setReceipts(r.data || []))
        .catch(() => {});
    }
  }, [invoiceId, isNew]);

  useEffect(() => {
    if (!isNew && invoiceId) {
      axios.get(`${API}/invoices/${invoiceId}/reminders`, { headers: getAuthHeaders() })
        .then(r => setReminders(r.data || []))
        .catch(() => {});
      axios.get(`${API}/invoices/${invoiceId}/pdf-versions`, { headers: getAuthHeaders() })
        .then(r => setPdfVersions(r.data || []))
        .catch(() => {});
    }
  }, [invoiceId, isNew]);

  // Load cancellation record
  useEffect(() => {
    if (!isNew && invoiceId) {
      axios.get(`${API}/cancellations/${invoiceId}`, { headers: getAuthHeaders() })
        .then(r => {
          setCancellationRecord(r.data);
          setCancellationForm({
            customer_refund_amount:   r.data.customer_refund_amount  != null ? String(r.data.customer_refund_amount)  : "",
            customer_refund_date:     r.data.customer_refund_date    || "",
            customer_refund_method:   r.data.customer_refund_method  || "",
            supplier_refund_amount:   r.data.supplier_refund_amount  != null ? String(r.data.supplier_refund_amount)  : "",
            supplier_refund_date:     r.data.supplier_refund_date    || "",
            supplier_refund_notes:    r.data.supplier_refund_notes   || "",
            cancellation_fee_retained: r.data.cancellation_fee_retained != null ? String(r.data.cancellation_fee_retained) : "",
            unrecovered_payment_fee:  r.data.unrecovered_payment_fee != null ? String(r.data.unrecovered_payment_fee) : "",
            refund_processing_fee:    r.data.refund_processing_fee   != null ? String(r.data.refund_processing_fee)   : "",
            cancellation_date:        r.data.cancellation_date       || "",
            vat_adjustment_amount:    r.data.vat_adjustment_amount   != null ? String(r.data.vat_adjustment_amount)   : "",
            cancellation_reason:      r.data.cancellation_reason     || "",
            internal_notes:           r.data.internal_notes          || "",
            refund_method:    r.data.refund_method    || "",
            refund_days:      r.data.refund_days      != null ? String(r.data.refund_days) : "",
            refund_days_type: r.data.refund_days_type || "",
          });
        })
        .catch(() => {}); // 404 = no record yet, that's fine
    }
  }, [invoiceId, isNew]);

  useEffect(() => {
    axios.get(`${API}/admin/payment-settings`, { headers: getAuthHeaders() })
      .then(r => setPaymentSettingsPreview(r.data))
      .catch(() => {});
  }, []);

  // ── Form handlers ────────────────────────────────────────────────────────────
  const setField = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    if (k === "vat_type" && VAT_NOTE_MAP[v]) next.vat_note = VAT_NOTE_MAP[v];
    return next;
  });

  // Line items
  const addLine = () => setLines(ls => [...ls, blankLine()]);
  const removeLine = idx => setLines(ls => ls.length <= 1 ? ls : ls.filter((_, i) => i !== idx));
  const updateLine = (idx, field, val) => setLines(ls => {
    const next = ls.map((li, i) => i !== idx ? li : { ...li, [field]: val });
    const li = next[idx];
    if (field === "quantity" || field === "unit_price") {
      const amt = computeLineAmount(field === "quantity" ? val : li.quantity, field === "unit_price" ? val : li.unit_price);
      next[idx] = { ...next[idx], amount: String(amt), vat_amount: String(computeLineVat(amt, li.vat_type)) };
    }
    if (field === "vat_type") {
      next[idx] = { ...next[idx], vat_amount: String(computeLineVat(parseFloat(li.amount) || 0, val)) };
    }
    if (field === "amount") {
      next[idx] = { ...next[idx], vat_amount: String(computeLineVat(parseFloat(val) || 0, li.vat_type)) };
    }
    return next;
  });

  // Payment schedule
  const applyPreset = () => {
    if (schedule.some(i => i.status === "paid")) {
      if (!window.confirm("This will replace the current schedule. Paid installments will be lost. Continue?")) return;
    }
    setSchedule(generateSchedule(schedulePreset, invoiceTotal, form.invoice_date));
  };

  const applyDiscount = () => {
    const val = parseFloat(discountValue);
    if (!val || val <= 0) { toast.error("Enter a discount amount greater than 0"); return; }
    const nonDiscountLines = lines.filter(l => l.line_type !== "discount");
    const baseAmount = round2(nonDiscountLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0));
    const discAmt = discountType === "percentage"
      ? round2(baseAmount * val / 100)
      : round2(val);
    if (discAmt <= 0) { toast.error("Discount amount must be positive"); return; }
    // Inherit VAT type from first non-discount line for correct VAT treatment
    const firstVatType = nonDiscountLines[0]?.vat_type || "";
    const vatAmt = firstVatType === "Dutch VAT 21%" ? round2(-discAmt * 0.21) : 0;
    const label = discountNote
      ? `${discountDesc} — ${discountNote}`
      : discountDesc;
    const discountLine = {
      id: uid(),
      description: label,
      quantity: "1",
      unit_price: String(-discAmt),
      amount: String(-discAmt),
      vat_type: firstVatType,
      vat_amount: String(vatAmt),
      line_type: "discount",
    };
    setLines(ls => [...ls, discountLine]);
    setDiscountValue("");
    setDiscountNote("");
    toast.success(`Discount of ${sym}${discAmt.toFixed(2)} applied`);
  };
  const addInstallment = () => setSchedule(s => [...s, blankInstallment()]);
  const removeInstallment = idx => setSchedule(s => s.filter((_, i) => i !== idx));
  const updateInstallment = (idx, field, val) => setSchedule(s => {
    const next = [...s];
    next[idx] = { ...next[idx], [field]: val };
    if (field === "amount" && invoiceTotal > 0) {
      next[idx].percentage = String(round2((parseFloat(val) || 0) / invoiceTotal * 100));
    }
    if (field === "percentage" && invoiceTotal > 0) {
      next[idx].amount = String(round2((parseFloat(val) || 0) / 100 * invoiceTotal));
    }
    return next;
  });

  const handleRecordPayment = async (installmentId) => {
    if (!invoiceId || isNew) { toast.error("Save the invoice first"); return; }
    setProcessingPayment(true);
    try {
      const res = await axios.post(
        `${API}/invoices/${invoiceId}/record-payment`,
        {
          installment_id:    installmentId,
          paid_amount:       parseFloat(paymentForm.paid_amount) || 0,
          paid_date:         paymentForm.paid_date,
          payment_method:    paymentForm.payment_method || null,
          payment_reference: paymentForm.payment_reference || null,
        },
        { headers: getAuthHeaders() }
      );
      const saved = res.data;
      setInvoice(saved);
      setForm(toDisplayForm(saved));
      setLines(toDisplayLines(saved));
      setSchedule(toDisplaySchedule(saved));
      setRecordingId(null);
      setPaymentForm({ paid_amount: "", paid_date: format(new Date(), "yyyy-MM-dd"), payment_method: "bank_transfer", payment_reference: "" });
      // Reload receipts
      axios.get(`${API}/invoices/${invoiceId}/receipts`, { headers: getAuthHeaders() })
        .then(r => setReceipts(r.data || [])).catch(() => {});
      toast.success("Payment recorded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payment");
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoiceId || isNew) { toast.error("Save the invoice first"); return; }
    setDownloadingPdf(true);
    try {
      const res = await axios.post(`${API}/invoices/${invoiceId}/pdf`, {}, {
        headers: { ...getAuthHeaders(), Accept: "application/pdf" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoice?.invoice_number || "invoice"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      axios.get(`${API}/invoices/${invoiceId}/pdf-versions`, { headers: getAuthHeaders() })
        .then(r => setPdfVersions(r.data || [])).catch(() => {});
    } catch {
      toast.error("PDF generation failed");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePreviewPdf = async () => {
    if (!invoiceId || isNew) { toast.error("Save the invoice first"); return; }
    setPreviewingPdf(true);
    try {
      const res = await axios.post(`${API}/invoices/${invoiceId}/pdf`, {}, {
        headers: { ...getAuthHeaders(), Accept: "application/pdf" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      axios.get(`${API}/invoices/${invoiceId}/pdf-versions`, { headers: getAuthHeaders() })
        .then(r => setPdfVersions(r.data || [])).catch(() => {});
    } catch {
      toast.error("PDF preview failed");
    } finally {
      setPreviewingPdf(false);
    }
  };

  const handleSendEmail = async (isTest) => {
    if (!invoiceId || isNew) { toast.error("Save the invoice first"); return; }
    if (!isTest) {
      setEmailPreview({
        type: "Invoice",
        recipient: form.customer_email,
        subject: `Invoice ${form.invoice_number || invoice?.invoice_number || ""} | The Better Fare`,
        attachment: `${form.invoice_number || invoice?.invoice_number || "invoice"}.pdf`,
        action: () => _doSendEmail(),
      });
      return;
    }
    await _doSendEmail(true);
  };

  const _doSendEmail = async (isTest = false) => {
    setSendingEmail(isTest ? "test" : "customer");
    try {
      const res = await axios.post(
        `${API}/invoices/${invoiceId}/send-email`,
        { is_test: isTest, recipient_email: form.customer_email || undefined },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message);
      if (!isTest) {
        setInvoice(i => i ? { ...i, invoice_sent_at: new Date().toISOString(), invoice_sent_to: form.customer_email } : i);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    } finally {
      setSendingEmail(null);
      setEmailPreview(null);
    }
  };

  const handleSendReminder = async (isTest) => {
    if (!invoiceId || isNew) { toast.error("Save the invoice first"); return; }
    if (!isTest) {
      const invNum = form.invoice_number || invoice?.invoice_number || "";
      setEmailPreview({
        type: "Payment Reminder",
        recipient: form.customer_email,
        subject: `Payment Reminder: Invoice ${invNum} | The Better Fare`,
        attachment: `${invNum}.pdf`,
        action: () => _doSendReminder(),
      });
      return;
    }
    await _doSendReminder(true);
  };

  const _doSendReminder = async (isTest = false) => {
    setSendingReminder(isTest ? "test" : "customer");
    try {
      const res = await axios.post(
        `${API}/invoices/${invoiceId}/send-reminder`,
        {
          is_test:        isTest,
          reminder_type:  reminderType,
          installment_id: reminderInstId || undefined,
          recipient_email: form.customer_email || undefined,
        },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message);
      axios.get(`${API}/invoices/${invoiceId}/reminders`, { headers: getAuthHeaders() })
        .then(r => setReminders(r.data || [])).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reminder failed");
    } finally {
      setSendingReminder(null);
      setEmailPreview(null);
    }
  };

  const handleSendCancellationDocEmail = () => {
    if (!cancellationRecord) return;
    const docNum = cancellationRecord.credit_note_number;
    const invNum = form.invoice_number || invoice?.invoice_number || "";
    setEmailPreview({
      type: "Credit Note",
      recipient: form.customer_email,
      subject: `Credit Note${docNum ? ` ${docNum}` : ""} – ${invNum} | The Better Fare`,
      attachment: `${docNum || "credit-note"}.pdf`,
      action: () => _doSendCancellationDocEmail(false),
    });
  };

  const handleSendCancellationDocTestEmail = async () => {
    if (!cancellationRecord) return;
    await _doSendCancellationDocEmail(true);
  };

  const _doSendCancellationDocEmail = async (isTest = false) => {
    if (isTest) setSendingDocTestEmail(true);
    else setSendingDocEmail(true);
    try {
      const res = await axios.post(
        `${API}/cancellations/${invoiceId}/send-credit-note-email`,
        { is_test: isTest, recipient_email: isTest ? undefined : (form.customer_email || undefined) },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message);
      if (!isTest) {
        const updated = await axios.get(`${API}/cancellations/${invoiceId}`, { headers: getAuthHeaders() });
        setCancellationRecord(updated.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    } finally {
      if (isTest) setSendingDocTestEmail(false);
      else setSendingDocEmail(false);
      setEmailPreview(null);
    }
  };

  const loadPaymentDefaults = async () => {
    setLoadingDefaults(true);
    try {
      const res = await axios.get(`${API}/admin/payment-settings`, { headers: getAuthHeaders() });
      const d = res.data;
      setForm(f => ({
        ...f,
        payment_methods:          d.methods?.length ? d.methods : f.payment_methods,
        payment_beneficiary_name: d.beneficiary_name || f.payment_beneficiary_name,
        payment_iban:             d.iban             || f.payment_iban,
        payment_bic:              d.bic              || f.payment_bic,
        payment_bank_name:        d.bank_name        || f.payment_bank_name,
        payment_tikkie_url:       d.tikkie_url       || f.payment_tikkie_url,
      }));
      toast.success("Payment defaults loaded");
    } catch {
      toast.error("Failed to load payment defaults");
    } finally {
      setLoadingDefaults(false);
    }
  };

  const handleGenerateTikkie = async () => {
    if (isNew || !invoiceId) { toast.error("Save the invoice first"); return; }
    setGeneratingTikkie(true);
    try {
      const res = await axios.post(
        `${API}/invoices/${invoiceId}/create-tikkie-payment`,
        {},
        { headers: getAuthHeaders() },
      );
      setInvoice(res.data);
      toast.success("Tikkie link generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate Tikkie link");
    } finally {
      setGeneratingTikkie(false);
    }
  };

  // ── Cancellation handlers ────────────────────────────────────────────────────
  const setCxField = (k, v) => setCancellationForm(f => ({ ...f, [k]: v }));

  const buildCancellationPayload = () => ({
    invoice_id:                invoiceId,
    customer_refund_amount:    parseFloat(cancellationForm.customer_refund_amount)    || 0,
    customer_refund_date:      cancellationForm.customer_refund_date      || null,
    customer_refund_method:    cancellationForm.customer_refund_method    || null,
    supplier_refund_amount:    parseFloat(cancellationForm.supplier_refund_amount)    || 0,
    supplier_refund_date:      cancellationForm.supplier_refund_date      || null,
    supplier_refund_notes:     cancellationForm.supplier_refund_notes     || null,
    cancellation_fee_retained: parseFloat(cancellationForm.cancellation_fee_retained) || 0,
    unrecovered_payment_fee:   parseFloat(cancellationForm.unrecovered_payment_fee)   || 0,
    refund_processing_fee:     parseFloat(cancellationForm.refund_processing_fee)     || 0,
    cancellation_date:         cancellationForm.cancellation_date         || null,
    vat_adjustment_amount:     cancellationForm.vat_adjustment_amount !== ""
                                 ? parseFloat(cancellationForm.vat_adjustment_amount)
                                 : null,
    cancellation_reason:       cancellationForm.cancellation_reason       || null,
    internal_notes:            cancellationForm.internal_notes            || null,
    refund_method:    cancellationForm.refund_method    || null,
    refund_days:      cancellationForm.refund_days !== ""
                        ? parseInt(cancellationForm.refund_days, 10) || null
                        : null,
    refund_days_type: cancellationForm.refund_days_type || null,
  });

  const saveCancellation = async () => {
    setCancellationSaving(true);
    try {
      const payload = buildCancellationPayload();
      let res;
      if (cancellationRecord) {
        res = await axios.patch(`${API}/cancellations/${invoiceId}`, payload, { headers: getAuthHeaders() });
      } else {
        res = await axios.post(`${API}/cancellations`, payload, { headers: getAuthHeaders() });
      }
      setCancellationRecord(res.data);
      toast.success(cancellationRecord ? "Cancellation record updated" : "Cancellation record created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save cancellation record");
    } finally {
      setCancellationSaving(false);
    }
  };

  const deleteCancellation = async () => {
    if (!window.confirm("Delete this cancellation record? The original invoice data is not affected.")) return;
    try {
      await axios.delete(`${API}/cancellations/${invoiceId}`, { headers: getAuthHeaders() });
      setCancellationRecord(null);
      setCancellationForm(BLANK_CANCELLATION_FORM);
      toast.success("Cancellation record deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const generateCancellationDocument = async (docType) => {
    if (!cancellationRecord) { toast.error("Save the cancellation record first"); return; }
    setGeneratingDoc(docType);
    try {
      const endpoint = docType === "credit_note"
        ? `${API}/cancellations/${invoiceId}/generate-credit-note`
        : `${API}/cancellations/${invoiceId}/generate-cancellation-doc`;
      const res = await axios.post(endpoint, {}, {
        headers: { ...getAuthHeaders(), Accept: "application/pdf" },
        responseType: "blob",
      });
      const docNum = res.headers["x-document-number"] || docType;
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${docNum}.pdf`; a.click();
      URL.revokeObjectURL(url);
      // Refresh record to get allocated document number
      const updated = await axios.get(`${API}/cancellations/${invoiceId}`, { headers: getAuthHeaders() });
      setCancellationRecord(updated.data);
    } catch (e) {
      toast.error("Failed to generate document");
    } finally {
      setGeneratingDoc(null);
    }
  };

  const previewCancellationDocument = async (docType) => {
    if (!cancellationRecord) { toast.error("Save the cancellation record first"); return; }
    setPreviewingDoc(docType);
    try {
      const res = await axios.get(`${API}/cancellations/${invoiceId}/preview-document`, {
        params: { doc_type: docType },
        headers: { ...getAuthHeaders(), Accept: "application/pdf" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Failed to generate preview");
    } finally {
      setPreviewingDoc(null);
    }
  };

  // ── Save / Delete ─────────────────────────────────────────────────────────────
  const buildPayload = () => ({
    invoice_number:   form.invoice_number   || undefined,
    source_type:      form.source_type,
    source_id:        form.source_id        || undefined,
    quote_reference:  form.quote_reference  || undefined,
    customer_name:    form.customer_name,
    customer_email:   form.customer_email   || undefined,
    customer_address: form.customer_address || undefined,
    invoice_date:     form.invoice_date,
    due_date:         form.due_date         || undefined,
    currency:         form.currency,
    invoice_total:    invoiceTotal,
    amount_received:  effectiveReceived,
    balance_due:      balanceDue,
    invoice_status:   form.invoice_status,
    payment_status:   form.payment_status,
    vat_type:         form.vat_type         || undefined,
    vat_amount:       vatTotal,
    vat_note:         form.vat_note         || undefined,
    notes:            form.notes            || undefined,
    external_invoice_source:    form.external_invoice_source    || undefined,
    external_invoice_reference: form.external_invoice_reference || undefined,
    // Payment methods: always saved (can be set without full override)
    payment_methods: form.payment_methods.length ? form.payment_methods : undefined,
    // Detail overrides: only stored when override is explicitly active
    ...(paymentOverride ? {
      payment_beneficiary_name:    form.payment_beneficiary_name    || undefined,
      payment_iban:                form.payment_iban                || undefined,
      payment_bic:                 form.payment_bic                 || undefined,
      payment_bank_name:           form.payment_bank_name           || undefined,
      payment_tikkie_url:          form.payment_tikkie_url          || undefined,
      payment_stripe_url:          form.payment_stripe_url          || undefined,
      payment_reference_override:  form.payment_reference_override  || undefined,
      payment_custom_instructions: form.payment_custom_instructions || undefined,
      pay_later_flight:            form.pay_later_flight            || undefined,
    } : {
      payment_beneficiary_name: null, payment_iban: null,
      payment_bic: null, payment_bank_name: null, payment_tikkie_url: null,
      payment_stripe_url: null, payment_reference_override: null,
      payment_custom_instructions: null, pay_later_flight: false,
    }),
    // Bookkeeping
    supplier_name:        form.supplier_name        || undefined,
    supplier_reference:   form.supplier_reference   || undefined,
    supplier_cost:        parseFloat(form.supplier_cost) || 0,
    payment_mode:         form.payment_mode         || undefined,
    supplier_paid_date:   form.supplier_paid_date   || undefined,
    date_received:        form.date_received        || undefined,
    bank_fee:             parseFloat(form.bank_fee) || 0,
    payment_processor:    form.payment_processor    || undefined,
    payout_date:          form.payout_date          || undefined,
    reconciled:           form.reconciled           || false,
    supplier_receipt_url: form.supplier_receipt_url || undefined,
    line_items: lines
      .filter(li => li.description || parseFloat(li.unit_price))
      .map(li => ({
        id: li.id, description: li.description,
        quantity: parseFloat(li.quantity) || 1,
        unit_price: parseFloat(li.unit_price) || 0,
        amount: parseFloat(li.amount) || 0,
        vat_type: li.vat_type || null,
        vat_amount: parseFloat(li.vat_amount) || 0,
        line_type: li.line_type || "other",
      })),
    payment_schedule: schedule.map(item => ({
      id: item.id, label: item.label,
      amount: parseFloat(item.amount) || 0,
      percentage: parseFloat(item.percentage) || 0,
      due_date: item.due_date || null,
      due_event: item.due_event || null,
      status: item.status,
      paid_date: item.paid_date || null,
      paid_amount: item.paid_amount != null ? parseFloat(item.paid_amount) : null,
      payment_method: item.payment_method || null,
      payment_reference: item.payment_reference || null,
    })),
  });

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (receiptFileRef.current) receiptFileRef.current.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setReceiptUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      const ext = file.name.includes(".") ? file.name.split(".").pop() : "pdf";
      try {
        const uploadRes = await axios.post(`${API}/uploads/receipt`, {
          file_data: base64,
          filename: `${form.invoice_number || invoiceId}.${ext}`,
          mime_type: file.type,
          folder: "supplier-receipts",
          invoice_number: form.invoice_number,
        }, { headers: getAuthHeaders() });
        if (uploadRes.data.url) {
          setField("supplier_receipt_url", uploadRes.data.url);
          if (!isNew) {
            await axios.patch(`${API}/invoices/${invoiceId}`,
              { supplier_receipt_url: uploadRes.data.url },
              { headers: getAuthHeaders() });
          }
          toast.success("Receipt uploaded ✓");
        } else {
          toast.error("Upload failed");
        }
      } catch {
        toast.error("Upload failed");
      }
      setReceiptUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.customer_name) { toast.error("Customer name is required"); return; }
    if (!form.invoice_date)  { toast.error("Invoice date is required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const res = await axios.post(`${API}/invoices`, buildPayload(), { headers: getAuthHeaders() });
        const saved = res.data;
        toast.success(`Invoice ${saved.invoice_number} created`);
        navigate(`/invoices/${saved.id}`, { replace: true });
      } else {
        const res = await axios.patch(`${API}/invoices/${invoiceId}`, buildPayload(), { headers: getAuthHeaders() });
        const saved = res.data;
        setInvoice(saved);
        setForm(toDisplayForm(saved));
        setLines(toDisplayLines(saved));
        setSchedule(toDisplaySchedule(saved));
        toast.success("Invoice saved");
      }
    } catch (e) {
      const _detail = e.response?.data?.detail;
      toast.error(typeof _detail === "string" ? _detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`, { headers: getAuthHeaders() });
      toast.success("Invoice deleted");
      navigate("/invoices");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const sourceLink = () => {
    if (!invoice?.source_id) return null;
    if (invoice.source_type === "flight_booking") return `/bookings/${invoice.source_id}`;
    if (invoice.source_type === "hotel_booking") return `/hotel-bookings/${invoice.source_id}`;
    if (invoice.source_type === "visa_quote") return `/visa-quotes/${invoice.source_id}`;
    return null;
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  if (loading) return (
    <DashboardLayout>
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    </DashboardLayout>
  );

  const sym = CURRENCY_SYMBOLS[form.currency] || form.currency;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl mx-auto">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/invoices")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-serif text-2xl font-bold text-slate-900">
                {isNew ? "New Invoice" : (invoice?.invoice_number || "Invoice")}
              </h1>
              {!isNew && invoice && (
                <>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    { draft: "bg-slate-100 text-slate-600", sent: "bg-blue-100 text-blue-700",
                      partially_paid: "bg-amber-100 text-amber-700", paid: "bg-green-100 text-green-700",
                      overdue: "bg-red-100 text-red-700", cancelled: "bg-slate-100 text-slate-400",
                    }[invoice.invoice_status] || "bg-slate-100 text-slate-600"
                  }`}>{invoice.invoice_status.replace(/_/g, " ")}</span>
                  {invoice.source_type && (
                    <span className="text-xs text-slate-400">
                      {SOURCE_TYPE_LABELS[invoice.source_type] || invoice.source_type}
                    </span>
                  )}
                </>
              )}
            </div>
            {!isNew && invoice?.created_at && (
              <p className="text-sm text-slate-400 mt-0.5">
                Created {format(new Date(invoice.created_at), "d MMM yyyy, HH:mm")}
              </p>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            {!isNew && (
              <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(true)}
                className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600">
                <Trash2 className="w-4 h-4 mr-1" />Delete
              </Button>
            )}
            {!isNew && (
              <Button variant="outline" size="sm"
                onClick={() => {
                  axios.get(`${API}/invoices/${invoiceId}/revisions`, { headers: getAuthHeaders() })
                    .then(r => { setRevisions(r.data || []); setShowRevisions(true); })
                    .catch(() => toast.error("Could not load revision history"));
                }}
                className="border-slate-300 text-slate-600 hover:bg-slate-50">
                <History className="w-4 h-4 mr-1" />History
              </Button>
            )}
            <Button size="sm" className="bg-purple-700 hover:bg-purple-800 text-white"
              onClick={handleSave} disabled={saving}>
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" />Save</>}
            </Button>
          </div>
        </div>

        {/* Delete confirm */}
        {showDeleteConfirm && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
              <p className="text-sm font-medium text-red-700">Delete this invoice permanently?</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
              <Button size="sm" onClick={handleDelete} disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white text-xs">
                {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : "Delete"}
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-5">

          {/* ── Left: Main Form ────────────────────────────────── */}
          <div className="col-span-2 space-y-5">

            {/* Source / Reference */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Source & Reference</h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Source Type</Label>
                  <Select value={form.source_type} onValueChange={v => setField("source_type", v)}>
                    <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(SOURCE_TYPE_LABELS).map(([v, l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Quote / Booking Reference</Label>
                  <div className="flex gap-1 mt-1">
                    <Input value={form.quote_reference}
                      onChange={e => setField("quote_reference", e.target.value)}
                      placeholder="TBF-375…"
                      className="h-9 text-sm font-mono flex-1" />
                    {!isNew && sourceLink() && (
                      <a href={sourceLink()} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center h-9 w-9 border rounded-md text-slate-400 hover:text-purple-600 shrink-0">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* External accounting — hidden by default */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowExternalAccounting(v => !v)}
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors">
                  <span className={`transition-transform ${showExternalAccounting ? "rotate-90" : ""}`}>▶</span>
                  External / Integration Accounting
                  {(form.external_invoice_source || form.external_invoice_reference) && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px]">set</span>
                  )}
                </button>
                {showExternalAccounting && (
                  <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100">
                    <div>
                      <Label className="text-xs text-slate-500">External Source</Label>
                      <Input value={form.external_invoice_source}
                        onChange={e => setField("external_invoice_source", e.target.value)}
                        placeholder="Revolut, Stripe, Mollie, Wise…"
                        className="mt-1 h-9 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">External Reference</Label>
                      <Input value={form.external_invoice_reference}
                        onChange={e => setField("external_invoice_reference", e.target.value)}
                        placeholder="INV-2024-001…"
                        className="mt-1 h-9 text-sm font-mono" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Customer */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Customer</h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Name *</Label>
                  <Input value={form.customer_name}
                    onChange={e => setField("customer_name", e.target.value)}
                    placeholder="Full name or company"
                    className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <Input type="email" value={form.customer_email}
                    onChange={e => setField("customer_email", e.target.value)}
                    placeholder="customer@email.com"
                    className="mt-1 h-9 text-sm" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500">Address</Label>
                  <Input value={form.customer_address}
                    onChange={e => setField("customer_address", e.target.value)}
                    placeholder="Street, City, Country"
                    className="mt-1 h-9 text-sm" />
                </div>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Invoice Details</h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Invoice Date *</Label>
                  <Input type="date" value={form.invoice_date}
                    onChange={e => setField("invoice_date", e.target.value)}
                    className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Due Date</Label>
                  <Input type="date" value={form.due_date}
                    onChange={e => setField("due_date", e.target.value)}
                    className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Currency</Label>
                  <Select value={form.currency} onValueChange={v => setField("currency", v)}>
                    <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">
                    Amount Received ({sym})
                    {hasSchedule && <span className="text-slate-400 font-normal ml-1">(from schedule)</span>}
                  </Label>
                  <Input type="number" step="0.01"
                    value={hasSchedule ? scheduleAmountReceived.toFixed(2) : form.amount_received}
                    onChange={e => !hasSchedule && setField("amount_received", e.target.value)}
                    readOnly={hasSchedule}
                    placeholder="0.00"
                    className={`mt-1 h-9 text-sm font-mono ${hasSchedule ? "bg-slate-50" : ""}`} />
                </div>
              </div>
            </div>

            {/* Bookkeeping */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              <button
                type="button"
                onClick={() => setShowBookkeeping(v => !v)}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors w-full">
                <span className={`transition-transform ${showBookkeeping ? "rotate-90" : ""}`}>▶</span>
                Bookkeeping
                {(form.supplier_name || parseFloat(form.supplier_cost) > 0 || form.supplier_receipt_url) && (
                  <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px]">set</span>
                )}
              </button>
              {showBookkeeping && (
                <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100">
                  <div>
                    <Label className="text-xs text-slate-500">Supplier</Label>
                    <Input value={form.supplier_name}
                      onChange={e => setField("supplier_name", e.target.value)}
                      placeholder="Airline, hotel, supplier…"
                      className="mt-1 h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Supplier #</Label>
                    <Input value={form.supplier_reference}
                      onChange={e => setField("supplier_reference", e.target.value)}
                      className="mt-1 h-9 text-sm font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Cost (€)</Label>
                    <Input type="number" step="0.01" value={form.supplier_cost}
                      onChange={e => setField("supplier_cost", e.target.value)}
                      placeholder="0.00"
                      className="mt-1 h-9 text-sm font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Bank Fee (€)</Label>
                    <Input type="number" step="0.01" value={form.bank_fee}
                      onChange={e => setField("bank_fee", e.target.value)}
                      placeholder="0.00"
                      className="mt-1 h-9 text-sm font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Paid With</Label>
                    <Input value={form.payment_mode}
                      onChange={e => setField("payment_mode", e.target.value)}
                      placeholder="Bank transfer, card…"
                      className="mt-1 h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Supplier Paid</Label>
                    <Input type="date" value={form.supplier_paid_date}
                      onChange={e => setField("supplier_paid_date", e.target.value)}
                      className="mt-1 h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Date Received</Label>
                    <Input type="date" value={form.date_received}
                      onChange={e => setField("date_received", e.target.value)}
                      className="mt-1 h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Amount Received (€)</Label>
                    <Input type="number" step="0.01" value={form.amount_received}
                      onChange={e => setField("amount_received", e.target.value)}
                      placeholder="0.00"
                      className="mt-1 h-9 text-sm font-mono" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Receipt</Label>
                    <div className="mt-1">
                      {form.supplier_receipt_url ? (
                        <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                          <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                          <span className="text-sm text-green-700 flex-1 truncate">Receipt attached</span>
                          <div className="flex items-center gap-1 shrink-0">
                            <a href={form.supplier_receipt_url} target="_blank" rel="noopener noreferrer"
                              className="text-blue-500 hover:text-blue-700" title="View receipt">
                              <Eye className="w-3.5 h-3.5" />
                            </a>
                            <button type="button" onClick={() => receiptFileRef.current?.click()}
                              disabled={receiptUploading}
                              className="text-slate-400 hover:text-purple-600" title="Replace receipt">
                              <Upload className="w-3.5 h-3.5" />
                            </button>
                            <button type="button" onClick={() => setField("supplier_receipt_url", "")}
                              className="text-red-400 hover:text-red-600" title="Remove receipt">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button type="button" onClick={() => receiptFileRef.current?.click()}
                          disabled={receiptUploading}
                          className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-400 hover:bg-purple-50 transition-colors disabled:opacity-50">
                          <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                          <span className="text-sm text-gray-500">{receiptUploading ? "Uploading…" : "Tap to upload supplier invoice / receipt"}</span>
                        </button>
                      )}
                      <input ref={receiptFileRef} type="file" accept="image/*,application/pdf"
                        className="hidden" onChange={handleReceiptUpload} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Cancellation / Refund */}
            {!isNew && (
              <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
                <button
                  type="button"
                  onClick={() => setShowCancellation(v => !v)}
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors w-full">
                  <span className={`transition-transform ${showCancellation ? "rotate-90" : ""}`}>▶</span>
                  Cancellation / Refund
                  {cancellationRecord && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-orange-100 text-orange-600 text-[10px]">active</span>
                  )}
                </button>
                {showCancellation && (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-4">
                    {cancellationRecord && (
                      <div className="flex gap-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-700 items-start">
                        <span className="shrink-0">⚠</span>
                        <span>
                          This invoice has an active cancellation record. It is the sole source of cancellation/refund data
                          in Revenue Transactions and VAT Report — legacy refund fields are ignored.
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Cancellation Date</Label>
                        <Input type="date" value={cancellationForm.cancellation_date}
                          onChange={e => setCxField("cancellation_date", e.target.value)}
                          className="mt-1 h-9 text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Customer Refund (€)</Label>
                        <Input type="number" step="0.01" value={cancellationForm.customer_refund_amount}
                          onChange={e => setCxField("customer_refund_amount", e.target.value)}
                          placeholder="0.00" className="mt-1 h-9 text-sm font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Customer Refund Date</Label>
                        <Input type="date" value={cancellationForm.customer_refund_date}
                          onChange={e => setCxField("customer_refund_date", e.target.value)}
                          className="mt-1 h-9 text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Customer Refund Method</Label>
                        <Input value={cancellationForm.customer_refund_method}
                          onChange={e => setCxField("customer_refund_method", e.target.value)}
                          placeholder="Bank transfer, Tikkie…"
                          className="mt-1 h-9 text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Supplier Refund (€)</Label>
                        <Input type="number" step="0.01" value={cancellationForm.supplier_refund_amount}
                          onChange={e => setCxField("supplier_refund_amount", e.target.value)}
                          placeholder="0.00" className="mt-1 h-9 text-sm font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Supplier Refund Date</Label>
                        <Input type="date" value={cancellationForm.supplier_refund_date}
                          onChange={e => setCxField("supplier_refund_date", e.target.value)}
                          className="mt-1 h-9 text-sm" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-slate-500">Supplier Refund Notes</Label>
                        <Input value={cancellationForm.supplier_refund_notes}
                          onChange={e => setCxField("supplier_refund_notes", e.target.value)}
                          className="mt-1 h-9 text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Cancellation Fee Retained (€)</Label>
                        <Input type="number" step="0.01" value={cancellationForm.cancellation_fee_retained}
                          onChange={e => setCxField("cancellation_fee_retained", e.target.value)}
                          placeholder="0.00" className="mt-1 h-9 text-sm font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Unrecovered Payment Fee (€)</Label>
                        <Input type="number" step="0.01" value={cancellationForm.unrecovered_payment_fee}
                          onChange={e => setCxField("unrecovered_payment_fee", e.target.value)}
                          placeholder="0.00 — additional cost from refund tx only, not original bank fee"
                          className="mt-1 h-9 text-sm font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Refund Processing Fee (€)</Label>
                        <Input type="number" step="0.01" value={cancellationForm.refund_processing_fee}
                          onChange={e => setCxField("refund_processing_fee", e.target.value)}
                          placeholder="0.00" className="mt-1 h-9 text-sm font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">VAT Adjustment Amount (€)</Label>
                        <Input type="number" step="0.01" value={cancellationForm.vat_adjustment_amount}
                          onChange={e => setCxField("vat_adjustment_amount", e.target.value)}
                          placeholder="Leave blank if no VAT adjustment needed"
                          className="mt-1 h-9 text-sm font-mono" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-slate-500">Cancellation Reason</Label>
                        <Input value={cancellationForm.cancellation_reason}
                          onChange={e => setCxField("cancellation_reason", e.target.value)}
                          placeholder="e.g. Customer request, visa denied, schedule incompatibility"
                          className="mt-1 h-9 text-sm" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-slate-500">Internal Notes</Label>
                        <Input value={cancellationForm.internal_notes}
                          onChange={e => setCxField("internal_notes", e.target.value)}
                          className="mt-1 h-9 text-sm" />
                      </div>
                      {/* Refund method & timing — appears on document when set */}
                      <div className="col-span-2">
                        <Label className="text-xs text-slate-500">Refund Method (optional — appears on document)</Label>
                        <Select value={cancellationForm.refund_method || ""}
                          onValueChange={v => setCxField("refund_method", v)}>
                          <SelectTrigger className="mt-1 h-9 text-sm">
                            <SelectValue placeholder="Not specified" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="">Not specified</SelectItem>
                            <SelectItem value="original_form_of_payment">Original form of payment</SelectItem>
                            <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {cancellationForm.refund_method && (
                        <>
                          <div>
                            <Label className="text-xs text-slate-500">Refund Timing (number of days)</Label>
                            <Input type="number" min="1" value={cancellationForm.refund_days}
                              onChange={e => setCxField("refund_days", e.target.value)}
                              placeholder="e.g. 7" className="mt-1 h-9 text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Days Type</Label>
                            <Select value={cancellationForm.refund_days_type || "calendar"}
                              onValueChange={v => setCxField("refund_days_type", v)}>
                              <SelectTrigger className="mt-1 h-9 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="calendar">Calendar days</SelectItem>
                                <SelectItem value="working">Working days</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Net position preview */}
                    {cancellationRecord && (
                      <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
                        <p className="font-semibold text-slate-600 mb-1">Cancellation Net Position</p>
                        {[
                          ["Customer Refund", -(parseFloat(cancellationForm.customer_refund_amount) || 0)],
                          ["Supplier Refund", +(parseFloat(cancellationForm.supplier_refund_amount) || 0)],
                          ["Fee Retained",    +(parseFloat(cancellationForm.cancellation_fee_retained) || 0)],
                          ["Unrecovered Fee", -(parseFloat(cancellationForm.unrecovered_payment_fee) || 0)],
                          ["Processing Fee",  -(parseFloat(cancellationForm.refund_processing_fee) || 0)],
                        ].map(([label, val]) => (
                          <div key={label} className="flex justify-between">
                            <span className="text-slate-500">{label}</span>
                            <span className={`font-mono ${val < 0 ? "text-red-600" : val > 0 ? "text-green-600" : "text-slate-400"}`}>
                              {val < 0 ? "−" : "+"}€{Math.abs(val).toFixed(2)}
                            </span>
                          </div>
                        ))}
                        {(() => {
                          const net = (
                            -(parseFloat(cancellationForm.customer_refund_amount) || 0)
                            + (parseFloat(cancellationForm.supplier_refund_amount) || 0)
                            + (parseFloat(cancellationForm.cancellation_fee_retained) || 0)
                            - (parseFloat(cancellationForm.unrecovered_payment_fee) || 0)
                            - (parseFloat(cancellationForm.refund_processing_fee) || 0)
                          );
                          return (
                            <div className="flex justify-between border-t border-slate-200 pt-1 mt-1 font-semibold">
                              <span className="text-slate-700">Net Position</span>
                              <span className={`font-mono ${net < 0 ? "text-red-700" : net > 0 ? "text-green-700" : "text-slate-500"}`}>
                                {net < 0 ? "−" : "+"}€{Math.abs(net).toFixed(2)}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex gap-2 flex-wrap pt-1">
                      <Button size="sm" onClick={saveCancellation} disabled={cancellationSaving}
                        className="text-xs h-8 bg-orange-600 hover:bg-orange-700 text-white">
                        {cancellationSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                        {cancellationRecord ? "Update Record" : "Create Record"}
                      </Button>
                      {cancellationRecord && (
                        <>
                          <>
                            <Button size="sm" variant="outline" onClick={() => previewCancellationDocument("credit_note")}
                              disabled={!!previewingDoc || !!generatingDoc} className="text-xs h-8 border-purple-200 text-purple-700 hover:bg-purple-50">
                              {previewingDoc === "credit_note" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Eye className="w-3 h-3 mr-1" />}
                              Preview
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => generateCancellationDocument("credit_note")}
                              disabled={!!generatingDoc || !!previewingDoc} className="text-xs h-8">
                              {generatingDoc === "credit_note" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                              {cancellationRecord.credit_note_number || "Generate Credit Note (TBF-CN)"}
                            </Button>
                            <Button size="sm" variant="outline" onClick={handleSendCancellationDocEmail}
                              disabled={sendingDocEmail || !form.customer_email} className="text-xs h-8">
                              <Send className="w-3 h-3 mr-1" />Send by Email
                            </Button>
                            <Button size="sm" variant="outline" onClick={handleSendCancellationDocTestEmail}
                              disabled={sendingDocTestEmail} className="text-xs h-8 text-amber-700 border-amber-300 hover:bg-amber-50">
                              {sendingDocTestEmail ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
                              Test → Admin
                            </Button>
                            {cancellationRecord.credit_note_sent_at && (
                              <span className="text-xs text-green-600 self-center">
                                Sent {format(new Date(cancellationRecord.credit_note_sent_at), "d MMM yyyy")}
                                {cancellationRecord.credit_note_sent_to && ` → ${cancellationRecord.credit_note_sent_to}`}
                              </span>
                            )}
                          </>
                          <Button size="sm" variant="ghost" onClick={deleteCancellation}
                            className="text-xs h-8 text-red-500 hover:text-red-700 ml-auto">
                            <Trash2 className="w-3 h-3 mr-1" />Delete Record
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Line Items */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Line Items</h2>
                <Button size="sm" variant="outline" onClick={addLine} className="text-xs h-7 px-2">
                  <Plus className="w-3 h-3 mr-1" />Add Line
                </Button>
              </div>

              <div className="space-y-3">
                {lines.map((li, idx) => (
                  <div key={li.id} className="bg-slate-50 rounded-lg border border-slate-200 p-3 space-y-2">
                    {/* Description row — full width */}
                    <div className="flex items-start gap-2">
                      <textarea
                        value={li.description}
                        onChange={e => updateLine(idx, "description", e.target.value)}
                        placeholder="Description (e.g. Visa service fee — United Kingdom Tourist / Visit)…"
                        rows={2}
                        className="flex-1 rounded-md border border-input bg-white px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                      <button onClick={() => removeLine(idx)} disabled={lines.length <= 1}
                        className="mt-1 flex items-center justify-center w-7 h-7 rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:pointer-events-none shrink-0">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {/* Fields row */}
                    <div className="grid gap-2 items-end" style={{ gridTemplateColumns: "1.4fr 0.8fr 1fr 1fr 1.4fr 0.9fr" }}>
                      <div>
                        <p className="text-[10px] text-slate-400 mb-0.5">Type</p>
                        <Select value={li.line_type} onValueChange={v => updateLine(idx, "line_type", v)}>
                          <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {LINE_TYPES.map(t => <SelectItem key={t} value={t}>{LINE_TYPE_LABELS[t]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 mb-0.5">Qty</p>
                        <Input type="number" step="1" min="0" value={li.quantity}
                          onChange={e => updateLine(idx, "quantity", e.target.value)}
                          className="h-7 text-xs font-mono min-w-[56px]" />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 mb-0.5">Unit Price</p>
                        <Input type="number" step="0.01" min="0" value={li.unit_price}
                          onChange={e => updateLine(idx, "unit_price", e.target.value)}
                          placeholder="0.00" className="h-7 text-xs font-mono" />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 mb-0.5">Amount</p>
                        <Input type="number" step="0.01" value={li.amount}
                          onChange={e => updateLine(idx, "amount", e.target.value)}
                          placeholder="0.00" className="h-7 text-xs font-mono bg-slate-100" />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 mb-0.5">VAT</p>
                        <Select value={li.vat_type || "__none__"}
                          onValueChange={v => updateLine(idx, "vat_type", v === "__none__" ? "" : v)}>
                          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="No VAT" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">No VAT</SelectItem>
                            {VAT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.value}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 mb-0.5">VAT Amt</p>
                        <Input type="number" step="0.01" value={li.vat_amount}
                          onChange={e => updateLine(idx, "vat_amount", e.target.value)}
                          placeholder="0.00" className="h-7 text-xs font-mono bg-slate-100" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {(() => {
                const discountLines    = lines.filter(l => l.line_type === "discount" || parseFloat(l.amount) < 0);
                const serviceLines     = lines.filter(l => l.line_type !== "discount" && parseFloat(l.amount) >= 0);
                const serviceSubtotal  = round2(serviceLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0));
                const discountSubtotal = round2(discountLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0));
                return (
                  <div className="border-t border-slate-100 pt-3 space-y-1">
                    {discountLines.length > 0 && (
                      <>
                        <div className="flex justify-end gap-4 text-sm">
                          <span className="text-slate-500">Services:</span>
                          <span className="font-mono font-semibold text-slate-700 w-28 text-right">{sym}{serviceSubtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-end gap-4 text-sm">
                          <span className="text-amber-600">Discount:</span>
                          <span className="font-mono font-semibold text-amber-600 w-28 text-right">-{sym}{Math.abs(discountSubtotal).toFixed(2)}</span>
                        </div>
                      </>
                    )}
                    {vatTotal > 0 && (
                      <div className="flex justify-end gap-4 text-sm">
                        <span className="text-slate-500">VAT:</span>
                        <span className="font-mono font-semibold text-slate-600 w-28 text-right">{sym}{vatTotal.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-end gap-4 text-sm border-t border-slate-200 pt-1">
                      <span className="font-semibold text-slate-700">Invoice Total:</span>
                      <span className="font-mono font-bold text-purple-700 w-28 text-right">{sym}{invoiceTotal.toFixed(2)}</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Discount */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Scissors className="w-3.5 h-3.5 text-amber-500" />
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Apply Discount</h2>
              </div>
              <p className="text-xs text-slate-400">
                Adds a visible negative line item so the customer can see exactly why the total changed.
                Original line items remain unchanged as audit history.
              </p>

              {/* Existing discount lines summary */}
              {lines.some(l => l.line_type === "discount" || parseFloat(l.amount) < 0) && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 space-y-1">
                  <p className="text-xs font-semibold text-amber-700">Active discounts on this invoice:</p>
                  {lines.filter(l => l.line_type === "discount" || parseFloat(l.amount) < 0).map((l, i) => (
                    <div key={l.id || i} className="flex justify-between text-xs text-amber-600">
                      <span>{l.description || "Discount"}</span>
                      <span className="font-mono">-{sym}{Math.abs(parseFloat(l.amount) || 0).toFixed(2)}</span>
                    </div>
                  ))}
                  <p className="text-[10px] text-amber-500 pt-1">Remove from the Line Items section above to undo a discount.</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {/* Discount type */}
                <div>
                  <Label className="text-xs text-slate-500">Discount Type</Label>
                  <Select value={discountType} onValueChange={setDiscountType}>
                    <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed Amount ({sym})</SelectItem>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Discount value */}
                <div>
                  <Label className="text-xs text-slate-500">
                    {discountType === "percentage" ? "Percentage (%)" : `Amount (${sym})`}
                    {discountType === "percentage" && discountValue && (() => {
                      const base = round2(lines.filter(l => l.line_type !== "discount" && parseFloat(l.amount) >= 0).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0));
                      const calc = round2(base * parseFloat(discountValue || 0) / 100);
                      return calc > 0 ? <span className="text-amber-600 ml-1">= {sym}{calc.toFixed(2)}</span> : null;
                    })()}
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={discountValue}
                    onChange={e => setDiscountValue(e.target.value)}
                    placeholder={discountType === "percentage" ? "e.g. 10" : "e.g. 29.00"}
                    className="mt-1 h-9 text-sm font-mono"
                  />
                </div>
                {/* Description */}
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500">Discount Label (shown on invoice)</Label>
                  <Input
                    value={discountDesc}
                    onChange={e => setDiscountDesc(e.target.value)}
                    placeholder="Discount / goodwill adjustment"
                    className="mt-1 h-9 text-sm"
                  />
                </div>
                {/* Note */}
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500">Internal Note (optional — appended to label)</Label>
                  <Input
                    value={discountNote}
                    onChange={e => setDiscountNote(e.target.value)}
                    placeholder="e.g. Goodwill discount agreed with customer"
                    className="mt-1 h-9 text-sm"
                  />
                </div>
              </div>

              <Button
                size="sm"
                onClick={applyDiscount}
                disabled={!discountValue || parseFloat(discountValue) <= 0}
                className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs px-4"
              >
                <Scissors className="w-3 h-3 mr-1.5" />
                Apply Discount
              </Button>
            </div>

            {/* Payment Schedule */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Payment Schedule</h2>
                <div className="flex items-center gap-2">
                  <Select value={schedulePreset} onValueChange={setSchedulePreset}>
                    <SelectTrigger className="h-8 text-xs w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SCHEDULE_PRESETS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={applyPreset} className="h-8 text-xs px-3">
                    Apply
                  </Button>
                </div>
              </div>

              {/* Warn when schedule total doesn't match invoice total (e.g. after applying a discount) */}
              {schedule.length > 0 && (() => {
                const schedTotal = round2(schedule.filter(i => i.status !== "waived").reduce((s, i) => s + (parseFloat(i.amount) || 0), 0));
                const diff = round2(invoiceTotal - schedTotal);
                if (Math.abs(diff) < 0.01) return null;
                return (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-amber-700">Payment schedule total mismatch</p>
                      <p className="text-xs text-amber-600 mt-0.5">
                        Invoice total is {sym}{invoiceTotal.toFixed(2)} but schedule sums to {sym}{schedTotal.toFixed(2)}.
                        Re-apply the preset or edit installment amounts to match the discounted total.
                      </p>
                    </div>
                  </div>
                );
              })()}

              {schedule.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-slate-200 rounded-xl text-slate-400">
                  <CalendarDays className="w-8 h-8 mb-2 opacity-40" />
                  <p className="text-sm">No payment schedule</p>
                  <p className="text-xs mt-0.5">Choose a preset above and click Apply</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {schedule.map((item, idx) => {
                    const sm = SCHED_STATUS_META[item.status] || SCHED_STATUS_META.pending;
                    const isRecording = recordingId === item.id;
                    const canRecord = (item.status === "pending" || item.status === "overdue") && !isNew && balanceDue > 0.005;
                    return (
                      <div key={item.id}>
                        {/* Installment card */}
                        <div className={`border rounded-xl p-3 space-y-2.5 ${
                          item.status === "paid"    ? "bg-green-50/60 border-green-200" :
                          item.status === "overdue" ? "bg-red-50/60 border-red-200" :
                          "bg-slate-50/50 border-slate-200"
                        }`}>
                          {/* Row 1: label, status, actions */}
                          <div className="flex items-center gap-2">
                            <Input value={item.label}
                              onChange={e => updateInstallment(idx, "label", e.target.value)}
                              disabled={item.status === "paid"}
                              className="h-7 text-xs font-medium flex-1 bg-white" />
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${sm.cls}`}>{sm.label}</span>
                            {item.status === "pending" && (
                              <button onClick={() => updateInstallment(idx, "status", "overdue")}
                                title="Mark overdue"
                                className="text-xs text-slate-400 hover:text-red-600 shrink-0 px-1">
                                Overdue
                              </button>
                            )}
                            {item.status !== "paid" && (
                              <button onClick={() => updateInstallment(idx, "status", "waived")}
                                title="Waive"
                                className="text-xs text-slate-400 hover:text-slate-600 shrink-0 px-1">
                                Waive
                              </button>
                            )}
                            <button onClick={() => removeInstallment(idx)}
                              className="text-slate-300 hover:text-red-500 shrink-0">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Row 2: amount, %, due date, due event */}
                          <div className="grid grid-cols-4 gap-2">
                            <div>
                              <Label className="text-xs text-slate-400">Amount ({sym})</Label>
                              <Input type="number" step="0.01"
                                value={item.amount}
                                onChange={e => updateInstallment(idx, "amount", e.target.value)}
                                disabled={item.status === "paid"}
                                placeholder="0.00"
                                className="h-7 text-xs font-mono mt-0.5 bg-white" />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-400">% of Total</Label>
                              <Input type="number" step="0.1"
                                value={item.percentage}
                                onChange={e => updateInstallment(idx, "percentage", e.target.value)}
                                disabled={item.status === "paid"}
                                placeholder="0"
                                className="h-7 text-xs font-mono mt-0.5 bg-white" />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-400">Due Date</Label>
                              <Input type="date"
                                value={item.due_date}
                                onChange={e => updateInstallment(idx, "due_date", e.target.value)}
                                disabled={item.status === "paid"}
                                className="h-7 text-xs mt-0.5 bg-white" />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-400">Due Event</Label>
                              <Input
                                value={item.due_event}
                                onChange={e => updateInstallment(idx, "due_event", e.target.value)}
                                disabled={item.status === "paid"}
                                placeholder="e.g. On acceptance"
                                className="h-7 text-xs mt-0.5 bg-white" />
                            </div>
                          </div>

                          {/* Paid details */}
                          {item.status === "paid" && item.paid_amount != null && (
                            <div className="flex flex-wrap items-center gap-2 text-xs text-green-700 pt-0.5">
                              <CheckCircle className="w-3.5 h-3.5 shrink-0" />
                              <span className="font-medium">{sym}{Number(item.paid_amount).toFixed(2)} received</span>
                              {item.paid_date && <span>· {item.paid_date}</span>}
                              {item.payment_method && (
                                <span>· {PAYMENT_METHODS.find(m => m.value === item.payment_method)?.label || item.payment_method}</span>
                              )}
                              {item.payment_reference && (
                                <span className="font-mono text-green-600">· ref: {item.payment_reference}</span>
                              )}
                            </div>
                          )}

                          {/* Record payment button */}
                          {canRecord && !isRecording && (
                            <Button size="sm" variant="outline"
                              onClick={() => {
                                setRecordingId(item.id);
                                setPaymentForm(f => ({
                                  ...f,
                                  paid_amount: String(parseFloat(item.amount) || ""),
                                  paid_date: format(new Date(), "yyyy-MM-dd"),
                                }));
                              }}
                              className="text-xs h-7 border-green-300 text-green-700 hover:bg-green-50">
                              Record Payment
                            </Button>
                          )}
                          {isNew && (item.status === "pending" || item.status === "overdue") && (
                            <p className="text-xs text-slate-400 italic">Save invoice first to record payments</p>
                          )}
                        </div>

                        {/* Inline record-payment form */}
                        {isRecording && (
                          <div className="border border-green-200 bg-green-50 rounded-xl p-4 mt-1 space-y-3">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-semibold text-green-800">
                                Record Payment — {item.label}
                              </p>
                              <span className="text-xs text-slate-500">
                                Remaining balance: <span className="font-mono font-semibold text-amber-700">{sym}{balanceDue.toFixed(2)}</span>
                              </span>
                            </div>
                            {paymentWouldOverpay && (
                              <div className="flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                <span className="font-semibold">Amount exceeds remaining balance.</span>
                                <span>Reduce to {sym}{balanceDue.toFixed(2)} or less.</span>
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs text-slate-500">Amount Paid ({sym})</Label>
                                <Input type="number" step="0.01"
                                  value={paymentForm.paid_amount}
                                  onChange={e => setPaymentForm(f => ({ ...f, paid_amount: e.target.value }))}
                                  className={`h-8 text-sm font-mono mt-1 ${paymentWouldOverpay ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Payment Date</Label>
                                <Input type="date"
                                  value={paymentForm.paid_date}
                                  onChange={e => setPaymentForm(f => ({ ...f, paid_date: e.target.value }))}
                                  className="h-8 text-sm mt-1" />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Payment Method</Label>
                                <Select value={paymentForm.payment_method}
                                  onValueChange={v => setPaymentForm(f => ({ ...f, payment_method: v }))}>
                                  <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {PAYMENT_METHODS.map(m => (
                                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Reference (optional)</Label>
                                <Input
                                  value={paymentForm.payment_reference}
                                  onChange={e => setPaymentForm(f => ({ ...f, payment_reference: e.target.value }))}
                                  placeholder="Bank ref, Tikkie ID…"
                                  className="h-8 text-sm mt-1" />
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm"
                                onClick={() => handleRecordPayment(item.id)}
                                disabled={processingPayment || paymentWouldOverpay || paymentEnteredAmt <= 0}
                                className="bg-green-600 hover:bg-green-700 text-white text-xs">
                                {processingPayment
                                  ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Saving…</>
                                  : <><CheckCircle className="w-3 h-3 mr-1" />Save Payment</>}
                              </Button>
                              <Button size="sm" variant="outline"
                                onClick={() => setRecordingId(null)} className="text-xs">
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <Button size="sm" variant="outline" onClick={addInstallment}
                    className="w-full text-xs border-dashed">
                    <Plus className="w-3 h-3 mr-1" />Add Installment
                  </Button>

                  {/* Schedule totals */}
                  <div className="border-t border-slate-100 pt-3 space-y-1">
                    {[
                      { label: "Total Scheduled", value: round2(schedule.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)), cls: "text-slate-600" },
                      { label: "Total Paid", value: scheduleAmountReceived, cls: "text-green-700" },
                      { label: "Remaining", value: round2(schedule.filter(i => i.status !== "paid" && i.status !== "waived").reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)), cls: "text-amber-700" },
                    ].map(r => (
                      <div key={r.label} className="flex justify-between text-xs">
                        <span className="text-slate-500">{r.label}:</span>
                        <span className={`font-mono font-medium ${r.cls}`}>{sym}{r.value.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* VAT */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">VAT</h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Invoice-Level VAT Treatment</Label>
                  <Select value={form.vat_type || "__none__"} onValueChange={v => setField("vat_type", v === "__none__" ? "" : v)}>
                    <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue placeholder="Not applicable" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Not applicable</SelectItem>
                      {VAT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.value}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400 mt-1">Used for the VAT statement printed on the invoice.</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Total VAT ({sym})</Label>
                  <Input readOnly value={vatTotal > 0 ? vatTotal.toFixed(2) : "0.00"}
                    className="mt-1 h-9 text-sm font-mono bg-slate-50" />
                  <p className="text-xs text-slate-400 mt-1">Computed from line items.</p>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">VAT Note (printed on invoice)</Label>
                <Input value={form.vat_note}
                  onChange={e => setField("vat_note", e.target.value)}
                  placeholder="Auto-populated when you select VAT treatment"
                  className="mt-1 h-9 text-sm" />
              </div>
            </div>

            {/* Notes */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Notes</h2>
              <textarea value={form.notes}
                onChange={e => setField("notes", e.target.value)}
                rows={3}
                placeholder="Internal notes or special instructions…"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring resize-none" />
            </div>

            {/* Payment Instructions */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              <div className="flex items-center gap-2">
                <CreditCard className="w-3.5 h-3.5 text-slate-400" />
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest flex-1">
                  Payment Instructions
                </h2>
                {paymentOverride && (
                  <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-medium">overridden</span>
                )}
              </div>

              <div className="space-y-3">

                {/* ── Default: "Customer will see" preview ─────────────── */}
                {!paymentOverride && (() => {
                  const ps = paymentSettingsPreview;
                  const effectiveTikkie = invoice?.payment_tikkie_url || ps?.tikkie_url || "";
                  const payRef = form.payment_reference_override || form.invoice_number;
                  const hasAnything = ps && (ps.iban || effectiveTikkie || ps.stripe_configured);
                  if (!ps) return (
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-xs text-slate-400">Loading payment configuration…</p>
                    </div>
                  );
                  if (!hasAnything) return (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-amber-700">No payment methods configured</p>
                        <p className="text-xs text-amber-600 mt-0.5">
                          No IBAN, Tikkie, or Stripe found in{" "}
                          <a href="/settings/payment" className="underline">Settings → Payment</a>.
                          The invoice PDF will have no payment instructions.
                        </p>
                      </div>
                    </div>
                  );
                  return (
                    <div className="space-y-3">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">What the customer will see:</p>

                      {/* Bank transfer block */}
                      {ps.iban && (
                        <div className="rounded-lg border border-slate-200 overflow-hidden text-xs">
                          <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-200">
                            <span className="font-semibold text-slate-500 uppercase tracking-widest text-[10px]">Bank Transfer</span>
                          </div>
                          <table className="w-full border-collapse">
                            <tbody>
                              {ps.beneficiary_name && (
                                <tr className="border-b border-slate-100">
                                  <td className="py-1.5 px-3 text-slate-400 w-28">Beneficiary</td>
                                  <td className="py-1.5 px-3 font-medium text-slate-700">{ps.beneficiary_name}</td>
                                </tr>
                              )}
                              <tr className="border-b border-slate-100">
                                <td className="py-1.5 px-3 text-slate-400">IBAN</td>
                                <td className="py-1.5 px-3 font-mono font-semibold text-slate-800">{ps.iban}</td>
                              </tr>
                              {ps.bic && (
                                <tr className="border-b border-slate-100">
                                  <td className="py-1.5 px-3 text-slate-400">BIC / SWIFT</td>
                                  <td className="py-1.5 px-3 font-mono font-semibold text-slate-800">{ps.bic}</td>
                                </tr>
                              )}
                              {ps.bank_name && (
                                <tr className="border-b border-slate-100">
                                  <td className="py-1.5 px-3 text-slate-400">Bank</td>
                                  <td className="py-1.5 px-3 text-slate-700">{ps.bank_name}</td>
                                </tr>
                              )}
                              {payRef && (
                                <tr>
                                  <td className="py-1.5 px-3 text-slate-400">Reference</td>
                                  <td className="py-1.5 px-3 font-mono font-semibold" style={{ color: "#1e3a5f" }}>{payRef}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Tikkie */}
                      {effectiveTikkie ? (
                        <div className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 bg-slate-50">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Tikkie</p>
                            <a href={effectiveTikkie} target="_blank" rel="noopener noreferrer"
                              className="text-xs font-mono text-blue-600 hover:underline truncate block">
                              {effectiveTikkie}
                            </a>
                          </div>
                        </div>
                      ) : ps.tikkie_api_configured ? (
                        <div className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 bg-slate-50">
                          <div className="flex-1">
                            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Tikkie</p>
                            <p className="text-xs text-slate-400">API connected — no link generated for this invoice yet</p>
                          </div>
                          {!isNew && (
                            <Button size="sm" variant="outline" className="h-7 text-xs shrink-0"
                              onClick={handleGenerateTikkie} disabled={generatingTikkie}>
                              {generatingTikkie ? <Loader2 className="w-3 h-3 animate-spin" /> : "Generate Link"}
                            </Button>
                          )}
                        </div>
                      ) : null}

                      {/* Stripe */}
                      {ps.stripe_configured && (
                        <div className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 bg-slate-50">
                          <div className="w-3.5 h-3.5 rounded-full bg-violet-500 shrink-0" />
                          <div>
                            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Stripe</p>
                            <p className="text-xs text-slate-400">API connected — paste a Stripe payment link in Override mode</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── Override mode ─────────────────────────────────────── */}
                {!paymentOverride && (
                  <Button size="sm" variant="outline" className="h-7 text-xs w-full"
                    onClick={() => { setPaymentOverride(true); loadPaymentDefaults(); }}>
                    <RefreshCw className="w-3 h-3 mr-1" />Override Payment Instructions for This Invoice
                  </Button>
                )}

                {paymentOverride && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-amber-700 flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          Override active — per-invoice payment details
                        </p>
                        <Button size="sm" variant="ghost" className="h-6 text-xs text-slate-400 hover:text-slate-600"
                          onClick={() => {
                            setPaymentOverride(false);
                            setField("payment_methods",          []);
                            setField("payment_beneficiary_name", "");
                            setField("payment_iban",             "");
                            setField("payment_bic",              "");
                            setField("payment_bank_name",        "");
                            setField("payment_tikkie_url",       "");
                            setField("payment_stripe_url",       "");
                            setField("payment_reference_override", "");
                            setField("payment_custom_instructions", "");
                          }}>
                          ✕ Remove Override
                        </Button>
                      </div>

                      {/* Payment methods */}
                      <div>
                        <Label className="text-xs text-slate-500 mb-2 block">Payment Methods to Show</Label>
                        <div className="flex flex-wrap gap-3">
                          {PAYMENT_METHOD_OPTIONS.map(opt => (
                            <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer text-sm text-slate-700">
                              <Checkbox
                                checked={form.payment_methods.includes(opt.value)}
                                onCheckedChange={checked => {
                                  setField("payment_methods",
                                    checked
                                      ? [...form.payment_methods, opt.value]
                                      : form.payment_methods.filter(m => m !== opt.value)
                                  );
                                }}
                              />
                              {opt.label}
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Bank transfer fields */}
                      {form.payment_methods.includes("bank_transfer") && (
                        <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <p className="text-xs font-semibold text-slate-500">Bank Transfer Details <span className="font-normal text-slate-400">(pre-filled from settings — edit to override)</span></p>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-slate-500">Beneficiary Name</Label>
                              <Input value={form.payment_beneficiary_name}
                                onChange={e => setField("payment_beneficiary_name", e.target.value)}
                                placeholder="The Better Fare"
                                className="mt-1 h-8 text-xs" />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">Bank Name</Label>
                              <Input value={form.payment_bank_name}
                                onChange={e => setField("payment_bank_name", e.target.value)}
                                placeholder="e.g. ING, Revolut…"
                                className="mt-1 h-8 text-xs" />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">IBAN</Label>
                              <Input value={form.payment_iban}
                                onChange={e => setField("payment_iban", e.target.value)}
                                placeholder="NL00 BANK 0000 0000 00"
                                className="mt-1 h-8 text-xs font-mono" />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">BIC / SWIFT</Label>
                              <Input value={form.payment_bic}
                                onChange={e => setField("payment_bic", e.target.value)}
                                placeholder="INGBNL2A"
                                className="mt-1 h-8 text-xs font-mono" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">
                              Payment Reference Override
                              <span className="font-normal text-slate-400 ml-1">(default: invoice number)</span>
                            </Label>
                            <Input value={form.payment_reference_override}
                              onChange={e => setField("payment_reference_override", e.target.value)}
                              placeholder="Leave blank to use invoice number"
                              className="mt-1 h-8 text-xs font-mono" />
                          </div>
                        </div>
                      )}

                      {/* Tikkie URL */}
                      {form.payment_methods.includes("tikkie") && (
                        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <Label className="text-xs text-slate-500">Tikkie Link URL
                            <span className="font-normal text-slate-400 ml-1">(pre-filled from settings — edit to use a different link)</span>
                          </Label>
                          <Input value={form.payment_tikkie_url}
                            onChange={e => setField("payment_tikkie_url", e.target.value)}
                            placeholder="https://tikkie.me/pay/…"
                            className="mt-1 h-8 text-xs" />
                        </div>
                      )}

                      {/* Stripe URL */}
                      {form.payment_methods.includes("stripe_link") && (
                        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <Label className="text-xs text-slate-500">Stripe / Online Payment URL
                            {paymentSettingsPreview?.stripe_configured
                              ? <span className="font-normal text-green-600 ml-1">Stripe API connected — enter the specific payment link URL</span>
                              : <span className="font-normal text-amber-600 ml-1">Stripe not connected — configure in Settings or paste a link below</span>
                            }
                          </Label>
                          <Input value={form.payment_stripe_url}
                            onChange={e => setField("payment_stripe_url", e.target.value)}
                            placeholder="https://buy.stripe.com/…"
                            className="mt-1 h-8 text-xs" />
                        </div>
                      )}

                      {/* Custom instructions */}
                      <div>
                        <Label className="text-xs text-slate-500">Custom Payment Instructions
                          <span className="font-normal text-slate-400 ml-1">(optional, added below bank details)</span>
                        </Label>
                        <textarea value={form.payment_custom_instructions}
                          onChange={e => setField("payment_custom_instructions", e.target.value)}
                          rows={2}
                          placeholder="Any additional payment notes for the customer…"
                          className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring resize-none" />
                      </div>

                      {/* Pay later flight toggle */}
                      <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                        <Checkbox
                          checked={form.pay_later_flight}
                          onCheckedChange={checked => setField("pay_later_flight", !!checked)}
                        />
                        <span>Flight booking — pay later
                          <span className="text-xs text-slate-400 ml-1">(adds: "Ticketing will be completed after payment confirmation")</span>
                        </span>
                      </label>
                    </div>
                  )}
                </div>

            </div>

            {/* Receipts */}
            {!isNew && (
              <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Receipts</h2>
                  <button
                    onClick={() => navigate("/receipts")}
                    className="text-xs text-purple-600 hover:underline">
                    View all receipts →
                  </button>
                </div>

                {receipts.length === 0 ? (
                  <div className="flex items-center gap-3 py-4 text-slate-400">
                    <ScrollText className="w-5 h-5 opacity-40 shrink-0" />
                    <p className="text-sm">No receipts yet — record a payment to generate one</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {receipts.map(r => {
                      const sym = CURRENCY_SYMBOLS[r.currency] || r.currency;
                      const METHOD_LABELS = {
                        bank_transfer: "Bank Transfer", card: "Card",
                        cash: "Cash", tikkie: "Tikkie", other: "Other",
                      };
                      return (
                        <div key={r.id}
                          onClick={() => navigate(`/receipts/${r.id}`)}
                          className="flex items-center justify-between p-3 bg-slate-50 hover:bg-purple-50/50 rounded-lg border border-slate-200 cursor-pointer transition-colors group">
                          <div className="flex items-center gap-3 min-w-0">
                            <ScrollText className="w-4 h-4 text-purple-500 shrink-0" />
                            <div className="min-w-0">
                              <p className="font-mono text-xs font-bold text-purple-700">
                                {r.receipt_number}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {r.payment_date
                                  ? format(new Date(r.payment_date), "d MMM yyyy")
                                  : "—"}
                                {r.installment_label && ` · ${r.installment_label}`}
                                {r.payment_method && ` · ${METHOD_LABELS[r.payment_method] || r.payment_method}`}
                              </p>
                            </div>
                          </div>
                          <div className="text-right shrink-0 ml-3">
                            <p className="font-mono text-sm font-semibold text-green-700">
                              {sym}{Number(r.amount_received).toFixed(2)}
                            </p>
                            <p className={`text-xs font-mono ${Number(r.remaining_balance) > 0 ? "text-amber-600" : "text-green-600"}`}>
                              bal: {sym}{Number(r.remaining_balance).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Review Request */}
            {!isNew && (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Customer Review</h2>
                </div>
                <ReviewRequestButton
                  sourceType="invoice"
                  sourceId={invoice?.id}
                  customerName={form.customer_name}
                  customerEmail={form.customer_email}
                />
              </div>
            )}

            {/* Reminders */}
            {!isNew && (
              <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-slate-400" />
                  <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Payment Reminders</h2>
                </div>

                {/* Compose reminder */}
                <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-slate-500">Reminder Type</Label>
                      <Select value={reminderType} onValueChange={setReminderType}>
                        <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unpaid_invoice">Unpaid Invoice</SelectItem>
                          <SelectItem value="due_installment">Due Installment</SelectItem>
                          <SelectItem value="overdue_installment">Overdue Installment</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {schedule.length > 0 && (
                      <div>
                        <Label className="text-xs text-slate-500">Installment (optional)</Label>
                        <Select value={reminderInstId || "__none__"} onValueChange={v => setReminderInstId(v === "__none__" ? "" : v)}>
                          <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="All / invoice" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">All / invoice level</SelectItem>
                            {schedule.map(item => (
                              <SelectItem key={item.id} value={item.id}>{item.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="text-xs h-7 flex-1"
                      onClick={() => handleSendReminder(true)} disabled={!!sendingReminder}>
                      {sendingReminder === "test"
                        ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        : <Mail className="w-3 h-3 mr-1" />}
                      Test Reminder → Admin
                    </Button>
                    <Button size="sm" className="text-xs h-7 flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={() => handleSendReminder(false)} disabled={!!sendingReminder || !form.customer_email}>
                      {sendingReminder === "customer"
                        ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        : <Send className="w-3 h-3 mr-1" />}
                      Send Reminder to Customer
                    </Button>
                  </div>
                </div>

                {/* Reminder history */}
                {reminders.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-400 font-medium">History</p>
                    {reminders.map(r => (
                      <div key={r.id} className="flex items-start justify-between gap-2 text-xs text-slate-500 py-1 border-b border-slate-50 last:border-0">
                        <div>
                          <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium mr-2 ${
                            r.is_test ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-700"
                          }`}>{r.is_test ? "TEST" : "SENT"}</span>
                          {r.reminder_type?.replace(/_/g, " ")}
                          {r.installment_label && ` · ${r.installment_label}`}
                        </div>
                        <div className="text-right shrink-0">
                          <p>{format(new Date(r.sent_at), "d MMM yyyy, HH:mm")}</p>
                          <p className="text-slate-400">{r.sent_to}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No reminders sent yet</p>
                )}
              </div>
            )}

          </div>

          {/* ── Right: Summary + Status ───────────────────────────── */}
          <div className="col-span-1 space-y-4">

            {/* Financial summary */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Summary</p>
              <table className="w-full text-xs border-collapse">
                <tbody>
                  <tr>
                    <td className="py-1 text-slate-500">Subtotal</td>
                    <td className="py-1 text-right font-mono text-slate-700">{sym}{subtotal.toFixed(2)}</td>
                  </tr>
                  {vatTotal > 0 && (
                    <tr>
                      <td className="py-1 text-slate-500">VAT</td>
                      <td className="py-1 text-right font-mono text-slate-600">{sym}{vatTotal.toFixed(2)}</td>
                    </tr>
                  )}
                  <tr className="border-t border-slate-200">
                    <td className="pt-2 pb-1 text-slate-700 font-semibold">Invoice Total</td>
                    <td className="pt-2 pb-1 text-right font-mono font-semibold text-slate-900">{sym}{invoiceTotal.toFixed(2)}</td>
                  </tr>
                  {hasSchedule && amountDueNow > 0 && (
                    <tr>
                      <td className="py-1 text-slate-500">Due Now</td>
                      <td className="py-1 text-right font-mono text-slate-700">{sym}{amountDueNow.toFixed(2)}</td>
                    </tr>
                  )}
                  {hasSchedule && futureAmount > 0 && (
                    <tr>
                      <td className="py-1 text-slate-400">Future</td>
                      <td className="py-1 text-right font-mono text-slate-400">{sym}{futureAmount.toFixed(2)}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="py-1 text-slate-500">Received</td>
                    <td className="py-1 text-right font-mono text-slate-600">{sym}{effectiveReceived.toFixed(2)}</td>
                  </tr>
                  <tr className="border-t border-slate-200">
                    <td className="pt-2 text-slate-700 font-semibold">Balance Due</td>
                    <td className={`pt-2 text-right font-mono font-bold text-sm ${
                      balanceDue > 0.005 ? "text-slate-900" : "text-slate-500"
                    }`}>
                      {sym}{balanceDue.toFixed(2)}
                      {balanceDue <= 0.005 && <span className="ml-1 text-[10px] font-normal text-green-600">✓ Paid</span>}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Statuses */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Status</p>
              <div>
                <Label className="text-xs text-slate-500">Invoice Status</Label>
                <Select value={form.invoice_status} onValueChange={v => setField("invoice_status", v)}>
                  <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INVOICE_STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Payment Status</Label>
                <Select value={form.payment_status} onValueChange={v => setField("payment_status", v)}>
                  <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Invoice # */}
            {!isNew && invoice && (
              <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Invoice #</p>
                <p className="font-mono text-lg font-bold text-purple-700">{invoice.invoice_number}</p>
                {invoice.updated_at && (
                  <p className="text-xs text-slate-400">
                    Last saved {format(new Date(invoice.updated_at), "d MMM yyyy, HH:mm")}
                  </p>
                )}
              </div>
            )}

            {/* PDF & Email actions */}
            {!isNew && (
              <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Actions</p>
                <Button size="sm" variant="outline" className="w-full justify-start text-xs"
                  onClick={handlePreviewPdf} disabled={previewingPdf || downloadingPdf}>
                  {previewingPdf
                    ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                    : <Eye className="w-3.5 h-3.5 mr-2" />}
                  Preview PDF
                </Button>
                <Button size="sm" variant="outline" className="w-full justify-start text-xs"
                  onClick={handleDownloadPdf} disabled={downloadingPdf || previewingPdf}>
                  {downloadingPdf
                    ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                    : <Download className="w-3.5 h-3.5 mr-2" />}
                  Download PDF
                </Button>
                <Button size="sm" variant="outline" className="w-full justify-start text-xs"
                  onClick={() => handleSendEmail(true)} disabled={!!sendingEmail}>
                  {sendingEmail === "test"
                    ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                    : <Mail className="w-3.5 h-3.5 mr-2" />}
                  Test Invoice Email → Admin
                </Button>
                <Button size="sm" className="w-full justify-start text-xs bg-purple-700 hover:bg-purple-800 text-white"
                  onClick={() => handleSendEmail(false)} disabled={!!sendingEmail || !form.customer_email}>
                  {sendingEmail === "customer"
                    ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                    : <Send className="w-3.5 h-3.5 mr-2" />}
                  Send Invoice to Customer
                </Button>
                {!form.customer_email && (
                  <p className="text-xs text-slate-400 italic">Add customer email to enable sending</p>
                )}
                {invoice?.invoice_sent_at && (
                  <p className="text-xs text-green-600">
                    Sent {format(new Date(invoice.invoice_sent_at), "d MMM yyyy")}
                    {invoice.invoice_sent_to && ` → ${invoice.invoice_sent_to}`}
                  </p>
                )}
              </div>
            )}

            {/* PDF version history */}
            {!isNew && pdfVersions.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                  <History className="w-3.5 h-3.5" />PDF History
                </p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {pdfVersions.map(v => (
                    <div key={v.version} className="flex items-center justify-between text-xs py-1 border-b border-slate-50 last:border-0">
                      <span className="text-slate-500">
                        v{v.version} · {v.generated_at ? format(new Date(v.generated_at), "d MMM yyyy, HH:mm") : "—"}
                      </span>
                      <span className="text-slate-400 text-[10px]">{v.generated_by || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Linked Flight Payment panel */}
            {!isNew && invoice?.source_type === "flight_booking" && linkedBooking?.linked && (
              <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 space-y-3">
                <p className="text-xs font-semibold text-blue-500 uppercase tracking-widest">Linked Flight Payment</p>

                {/* Booking payment status */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Booking</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    linkedBooking.booking_payment_status === "paid"              ? "bg-green-100 text-green-700" :
                    linkedBooking.booking_payment_status === "awaiting_payment"  ? "bg-amber-100 text-amber-700" :
                    linkedBooking.booking_payment_status === "pay_later"         ? "bg-blue-100 text-blue-700"   :
                    "bg-slate-100 text-slate-500"
                  }`}>
                    {linkedBooking.booking_payment_status?.replace(/_/g, " ") || "—"}
                  </span>
                </div>

                {/* Invoice payment status */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Invoice</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    linkedBooking.invoice_payment_status === "paid"           ? "bg-green-100 text-green-700" :
                    linkedBooking.invoice_payment_status === "partially_paid" ? "bg-amber-100 text-amber-700" :
                    "bg-slate-100 text-slate-500"
                  }`}>
                    {linkedBooking.invoice_payment_status?.replace(/_/g, " ") || "—"}
                  </span>
                </div>

                {/* Pay-later due date */}
                {linkedBooking.booking_pay_later && linkedBooking.booking_pay_later_due_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Due by</span>
                    <span className="text-xs font-mono text-slate-700">{linkedBooking.booking_pay_later_due_date}</span>
                  </div>
                )}

                {/* Amounts */}
                <div className="border-t border-blue-200 pt-2 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Received</span>
                    <span className="text-xs font-mono text-green-700">€{(linkedBooking.booking_accounting_amount_received || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Balance due</span>
                    <span className={`text-xs font-mono font-semibold ${linkedBooking.invoice_balance_due > 0.005 ? "text-amber-700" : "text-green-700"}`}>
                      €{(linkedBooking.invoice_balance_due || 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Tikkie link */}
                {linkedBooking.booking_payment_url && (
                  <a
                    href={linkedBooking.booking_payment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-purple-700 hover:text-purple-900 font-medium mt-1"
                  >
                    <CreditCard className="w-3 h-3" />
                    Open Tikkie payment link
                  </a>
                )}

                {/* Link to booking */}
                {invoice?.source_id && (
                  <a
                    href={`/bookings/${invoice.source_id}`}
                    className="block text-xs text-blue-600 hover:underline mt-1"
                  >
                    → Open booking
                  </a>
                )}

                <p className="text-xs text-slate-400">Payment data synced from booking. Marking paid here updates booking accounting.</p>
              </div>
            )}

            {/* Source link */}
            {!isNew && invoice?.source_id && (
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-1">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Linked Source</p>
                <p className="text-sm text-slate-700">{SOURCE_TYPE_LABELS[invoice.source_type] || invoice.source_type}</p>
                {invoice.quote_reference && (
                  <p className="font-mono text-xs text-slate-500">{invoice.quote_reference}</p>
                )}
                {sourceLink() && (
                  <a href={sourceLink()} className="block text-xs text-purple-600 hover:underline mt-1">
                    → Open {SOURCE_TYPE_LABELS[invoice.source_type] || "source"}
                  </a>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Revision History Dialog (P4) */}
      <Dialog open={showRevisions} onOpenChange={setShowRevisions}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <History className="w-4 h-4 text-slate-500" />
              Invoice Revision History
            </DialogTitle>
          </DialogHeader>
          {revisions.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No revision history yet. Revisions are recorded on every save after this update.</p>
          ) : (
            <div className="space-y-3 mt-1">
              {revisions.map(rev => {
                const snap = rev.invoice_snapshot || {};
                return (
                  <div key={rev.id} className="border border-slate-200 rounded-lg p-4 space-y-2 bg-slate-50">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
                        Revision #{rev.revision_number}
                      </span>
                      <span className="text-xs text-slate-400">
                        {rev.created_at ? format(new Date(rev.created_at), "d MMM yyyy HH:mm") : "—"}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div>
                        <span className="text-slate-400">Invoice Total</span>
                        <p className="font-mono font-semibold text-slate-700">
                          {snap.currency || "€"} {Number(snap.invoice_total || 0).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-400">Payment Status</span>
                        <p className="font-medium text-slate-700 capitalize">{snap.payment_status?.replace(/_/g, " ") || "—"}</p>
                      </div>
                      <div>
                        <span className="text-slate-400">Amount Received</span>
                        <p className="font-mono text-slate-700">{Number(snap.amount_received || 0).toFixed(2)}</p>
                      </div>
                      <div>
                        <span className="text-slate-400">Invoice Status</span>
                        <p className="font-medium text-slate-700 capitalize">{snap.invoice_status || "—"}</p>
                      </div>
                      <div>
                        <span className="text-slate-400">VAT Type</span>
                        <p className="text-slate-700">{snap.vat_type || "—"}</p>
                      </div>
                      <div>
                        <span className="text-slate-400">Saved by</span>
                        <p className="text-slate-700 font-mono text-[11px]">{rev.created_by || "—"}</p>
                      </div>
                    </div>
                    {(snap.notes || snap.payment_custom_instructions) && (
                      <p className="text-xs text-slate-500 italic truncate">
                        Notes: {snap.notes || snap.payment_custom_instructions}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Communication history */}
      {!isNew && invoice?.id && (
        <div className="max-w-4xl mx-auto px-4 pb-8">
          <CommunicationHistory
            sourceType="invoice"
            sourceId={invoice.id}
            bookingId={invoice.source_type === "flight_booking" ? invoice.source_id : undefined}
          />
        </div>
      )}

      {/* Email send preview / confirm modal */}
      {emailPreview && (
        <Dialog open onOpenChange={() => setEmailPreview(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-base">Confirm: Send {emailPreview.type}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2 text-sm text-slate-700">
              <div className="flex gap-2">
                <span className="text-slate-400 w-24 shrink-0">Type</span>
                <span className="font-medium">{emailPreview.type}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-slate-400 w-24 shrink-0">Recipient</span>
                <span className="font-mono text-purple-700">{emailPreview.recipient || "—"}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-slate-400 w-24 shrink-0">Subject</span>
                <span className="italic text-slate-600">{emailPreview.subject}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-slate-400 w-24 shrink-0">Attachment</span>
                <span className="font-mono text-xs text-slate-600">{emailPreview.attachment}</span>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" size="sm" onClick={() => setEmailPreview(null)}>Cancel</Button>
              <Button
                size="sm"
                className="bg-purple-700 hover:bg-purple-800 text-white"
                disabled={!!sendingEmail || !!sendingReminder || sendingDocEmail || sendingDocTestEmail}
                onClick={emailPreview.action}>
                {(sendingEmail === "customer" || sendingReminder === "customer" || sendingDocEmail || sendingDocTestEmail)
                  ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                  : <Send className="w-3.5 h-3.5 mr-2" />}
                Send Now
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </DashboardLayout>
  );
}
